import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, parseISO,
} from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookingStatusLegend } from "@/lib/bookingStatus";
import IcalConfigDialog from "@/components/admin/IcalConfigDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import useDismissible from "@/lib/useDismissible";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Calendar as CalIcon, BedDouble,
  LogIn, LogOut, Compass, AlertTriangle, AlertCircle, RefreshCcw, Plus, Save, FileEdit,
  Mail, Phone, Users as UsersIcon, Star, Sparkles, Car, MapPin, ExternalLink,
  ChevronDown, ChevronUp, Coffee, Baby, Dog, ParkingCircle, CreditCard,
  Wallet, Receipt, CheckCircle2, Clock, StickyNote, Link2,
  UserPlus, CornerUpLeft, Trash2, Eye, ReceiptText, Copy, FileText, Download, X,
} from "lucide-react";
import { formatDate } from "@/lib/dates";
import BookingPill from "@/components/admin/BookingPill";
import UnassignedWalkinsBanner from "@/components/admin/UnassignedWalkinsBanner";
import { DateInputIT } from "@/components/ui/date-input-it";

const WEEK_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const ISO = (d) => format(d, "yyyy-MM-dd");

const _str = (v) => (
  typeof v === "string" ? v
  : v && typeof v === "object" ? (v.it || v.en || Object.values(v)[0] || "")
  : ""
) || "";

const STATUS_DOT = {
  confirmed: "bg-emerald-500",
  completed: "bg-violet-500",
  pending: "bg-amber-400",
  checked_in: "bg-amber-500",
  rejected: "bg-rose-300",
  cancelled: "bg-slate-300",
  frozen: "bg-sky-400",
};
const STATUS_LABEL = {
  confirmed: "Confermata", completed: "Pagata", pending: "In attesa",
  checked_in: "Check-in fatto",
  rejected: "Rifiutata", cancelled: "Annullata", frozen: "Congelata",
};
const EVENT_TONE = {
  event: "bg-violet-100 text-violet-800 border-violet-300",
  activity: "bg-emerald-100 text-emerald-800 border-emerald-300",
  food: "bg-amber-100 text-amber-800 border-amber-300",
  beach: "bg-sky-100 text-sky-800 border-sky-300",
  default: "bg-slate-100 text-slate-700 border-slate-300",
};

const PAYMENT_LABEL = {
  paypal: "PayPal", bonifico: "Bonifico", sumup: "SumUp",
  satispay: "Satispay", postepay: "PostePay", carta: "Carta",
  contanti: "Contanti",
  booking_com: "Booking.com", airbnb: "Airbnb",
};
const PAYMENT_TONE = {
  paypal: "bg-blue-50 text-blue-700 border-blue-200",
  bonifico: "bg-slate-50 text-slate-700 border-slate-200",
  sumup: "bg-sky-50 text-sky-700 border-sky-200",
  satispay: "bg-rose-50 text-rose-700 border-rose-200",
  postepay: "bg-amber-50 text-amber-700 border-amber-200",
  carta: "bg-indigo-50 text-indigo-700 border-indigo-200",
  contanti: "bg-emerald-50 text-emerald-700 border-emerald-200",
  booking_com: "bg-blue-100 text-blue-800 border-blue-300",
  airbnb: "bg-rose-100 text-rose-800 border-rose-300",
};
const PARKING_LABEL = {
  none: "Nessuno",
  free_street: "Parcheggio libero in strada",
  paid_onsite: "Parcheggio a pagamento on site",
  paid_advance: "Parcheggio prenotato in anticipo",
  paid_private: "Parcheggio privato",
  paid_garage: "🏠 Garage / Custodito",
  paid_outdoor: "🌳 Esterno / Custodito",
};

// Build a unified list of extras for display: [{icon, label, price, free}]
const buildExtrasList = (b) => {
  const ex = b.extras || {};
  const nights = Number(b.nights || 1);
  const items = [];
  if (ex.crib) items.push({ icon: Baby, label: "Culla", price: 0, free: true });
  if (ex.extra_bed > 0) {
    items.push({
      icon: BedDouble,
      label: `Letto aggiuntivo × ${ex.extra_bed}`,
      price: ex.extra_bed * 15 * nights,
      detail: `€15/notte × ${nights} notti`,
    });
  }
  if (ex.pets) items.push({ icon: Dog, label: "Animale domestico (pulizia ipoallergenica)", price: 10 });
  if (ex.breakfast_celiac) items.push({ icon: Coffee, label: "Colazione celiaca", price: 0, free: true });
  if (ex.parking_choice && ex.parking_choice !== "none") {
    const isPaid = ["paid_advance", "paid_private", "paid_garage", "paid_outdoor"].includes(ex.parking_choice);
    items.push({
      icon: ParkingCircle,
      label: PARKING_LABEL[ex.parking_choice] || ex.parking_choice,
      price: isPaid ? 5 * nights : 0,
      free: !isPaid,
      detail: isPaid ? `€5/notte × ${nights} notti` : undefined,
    });
  }
  const trips = ex.shuttle_requests || [];
  trips.forEach((t, i) => {
    items.push({
      icon: Car,
      label: `Navetta ${i + 1}: ${t.from || "?"} → ${t.to || "?"}`,
      price: 0,
      free: true,
      detail: [t.date, t.time].filter(Boolean).join(" "),
    });
  });
  return items;
};

export default function CalendarTab({ onOpenBooking } = {}) {
  const navigate = useNavigate();
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(ISO(new Date()));
  // Ref to today's calendar cell — auto-scrolled into view on mount and
  // whenever the user clicks "Oggi". Makes today immediately visible without
  // manual scrolling on tall monitors.
  const todayCellRef = useRef(null);

  // iter223 — CACHE SESSIONE per rendering istantaneo del calendario.
  // Prima aprendo il calendario si vedeva vuoto per 1-3 secondi mentre
  // partivano 5 chiamate API in parallelo. Ora carichiamo l'ultimo stato
  // conosciuto da sessionStorage (se disponibile) e mostriamo subito i
  // pallini colorati; l'API refresh gira in background e sovrascrive
  // appena arrivano i nuovi dati. Cache TTL implicito = durata sessione
  // (svuotata alla chiusura del browser) → mai stale oltre la giornata.
  const CACHE_KEY = "cal_tab_cache_v1";
  const readCache = () => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch { /* ignore corrupted cache */ }
    return null;
  };
  const initialCache = readCache();

  const [bookings, setBookings] = useState(() => initialCache?.bookings || []);
  const [externals, setExternals] = useState(() => initialCache?.externals || []);
  const [duplicateGroups, setDuplicateGroups] = useState(() => initialCache?.duplicateGroups || []);
  // iter200 — X per chiudere il banner Duplicati per 7 giorni. La chiave
  // include il conteggio: se arrivano NUOVI duplicati oltre quelli già visti,
  // il banner riappare per richiamare l'attenzione.
  const [dupDismissed, dismissDup] = useDismissible(`duplicates-${duplicateGroups.length}`, 7);
  const [rooms, setRooms] = useState(() => initialCache?.rooms || []);
  const [guideEvents, setGuideEvents] = useState(() => initialCache?.guideEvents || []);
  const [guideError, setGuideError] = useState(null);
  // iter200 — X per chiudere l'avviso Guida Digitale offline per 7 giorni.
  const [guideDismissed, dismissGuide] = useDismissible("guide-offline", 7);
  // Loading indicator: solo per la rotellina sul bottone Aggiorna, non
  // blocca più la visualizzazione. Se abbiamo cache mostriamo subito.
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [newDate, setNewDate] = useState(ISO(new Date()));
  const [icalDialogOpen, setIcalDialogOpen] = useState(false);
  const [icalRooms, setIcalRooms] = useState([]);
  // User preference: hide OTA block entries in the Day Detail panel.
  // Persisted in localStorage so the choice survives reloads.
  const [hideOtaBlocks, setHideOtaBlocks] = useState(() => {
    try { return localStorage.getItem("cal_hide_ota_blocks") === "1"; }
    catch { return false; }
  });
  const toggleHideBlocks = () => {
    setHideOtaBlocks((v) => {
      const nv = !v;
      try { localStorage.setItem("cal_hide_ota_blocks", nv ? "1" : "0"); } catch { /* ignore */ }
      return nv;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    const fromISO = ISO(startOfMonth(month));
    const toISO = ISO(endOfMonth(month));
    try {
      const [b, r, gc, ext, dup] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/rooms").catch(() => ({ data: [] })),
        api.get("/admin/guide-calendar", { params: { from_date: fromISO, to_date: toISO } }).catch(
          (e) => ({ data: { ok: false, events: [], error: e.response?.data?.detail || e.message } }),
        ),
        api.get("/admin/external-bookings", { params: { from_date: fromISO, to_date: toISO } })
          .catch(() => ({ data: [] })),
        api.get("/admin/bookings/duplicates").catch(() => ({ data: { groups: [] } })),
      ]);
      const nextBookings = b.data || [];
      const nextRooms = r.data || [];
      const nextGuide = gc.data?.events || [];
      const nextExternals = ext.data || [];
      const nextDup = dup.data?.groups || [];
      setBookings(nextBookings);
      setRooms(nextRooms);
      setGuideEvents(nextGuide);
      setGuideError(gc.data?.ok === false ? (gc.data.error || null) : null);
      setExternals(nextExternals);
      setDuplicateGroups(nextDup);
      // iter223 — Save fresh snapshot for next mount.
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
          bookings: nextBookings,
          rooms: nextRooms,
          guideEvents: nextGuide,
          externals: nextExternals,
          duplicateGroups: nextDup,
          savedAt: Date.now(),
        }));
      } catch { /* sessionStorage quota exceeded — best effort */ }
    } catch {
      toast.error("Errore caricamento calendario");
    } finally {
      setLoading(false);
    }
  }, [month]);

  const autoMergeAll = useCallback(async (forceCrossRoom = false) => {
    const groups = duplicateGroups || [];
    if (!groups.length) return;
    const total = groups.reduce((s, g) => s + Math.max((g.count || g.bookings?.length || 1) - 1, 0), 0);
    const crossRoomGroups = groups.filter((g) => g.cross_room).length;
    let warn = `Risolvere ${groups.length} gruppi di duplicati automaticamente?\n\n` +
      `Verranno effettuati ~${total} merge.\n` +
      `Per ogni gruppo verrà tenuto il record più completo (con email/telefono/numero camera).\n` +
      `I record uniti vengono spostati nel cestino (recuperabili).`;
    if (crossRoomGroups > 0 && forceCrossRoom) {
      warn += `\n\n⚠️ ATTENZIONE: ${crossRoomGroups} gruppi hanno camere DIVERSE (forse prenotazioni distinte). Procedere comunque?`;
    } else if (crossRoomGroups > 0) {
      warn += `\n\nℹ️ ${crossRoomGroups} gruppi con camere diverse saranno saltati per sicurezza (usa "Forza unione" per includerli).`;
    }
    if (!window.confirm(warn)) return;
    try {
      const r = await api.post(`/admin/bookings/auto-merge-all${forceCrossRoom ? "?force_cross_room=true" : ""}`);
      const { groups_resolved = 0, merges = 0, groups_skipped_cross_room = 0 } = r.data || {};
      let msg = `✓ Risolti ${groups_resolved} gruppi (${merges} unioni)`;
      if (groups_skipped_cross_room > 0) msg += ` · ${groups_skipped_cross_room} saltati (camere diverse)`;
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(`Errore auto-merge: ${errMsg(e)}`);
    }
  }, [duplicateGroups, load]);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll today's cell into view once data and DOM are settled.
  // Runs whenever the visible month changes OR the data finishes loading.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      try {
        todayCellRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (e) {
        // ScrollIntoView with options is widely supported; no-op fallback.
      }
    }, 200);
    return () => clearTimeout(t);
  }, [loading, month]);

  const dayIndex = useMemo(() => {
    const idx = {};
    const add = (iso, k, v) => {
      if (!idx[iso]) idx[iso] = { checkins: [], checkouts: [], staying: [], events: [], rooms_busy: new Set() };
      idx[iso][k].push(v);
    };
    for (const b of bookings) {
      if (!["confirmed", "completed", "pending", "checked_in"].includes(b.status)) continue;
      add(b.check_in, "checkins", b);
      add(b.check_out, "checkouts", b);
      let d = new Date(b.check_in);
      const end = new Date(b.check_out);
      while (d < end) {
        const iso = ISO(d);
        if (!idx[iso]) idx[iso] = { checkins: [], checkouts: [], staying: [], events: [], rooms_busy: new Set() };
        idx[iso].rooms_busy.add(b.room_id || b.room_name);
        if (iso !== b.check_in) add(iso, "staying", b);
        d.setDate(d.getDate() + 1);
      }
    }
    // Include external (OTA iCal) bookings in occupancy counts so the
    // "X/Y occupate" badge reflects Airbnb/Booking holds (both real
    // reservations AND host-set channel blocks — both genuinely make
    // the unit unavailable on that channel).
    // iter124: SKIP externals that are "shadowed" by an internal booking on
    // the same room+overlapping dates — those are just OTA mirrors of a
    // reservation we already have internally (e.g. after merging a CSV-import
    // with an iCal stub). Showing both would double-count and clutter the cal.
    for (const e of (externals || [])) {
      if (!e.start || !e.end) continue;
      if (e.shadowed_by_internal) continue;
      const srcRaw = String(e.source || "").toLowerCase();
      const srcKey = srcRaw.includes("airbnb")
        ? "airbnb"
        : (srcRaw.includes("booking") ? "booking" : "external");
      let d = new Date(e.start);
      const end = new Date(e.end);
      while (d < end) {
        const iso = ISO(d);
        if (!idx[iso]) idx[iso] = { checkins: [], checkouts: [], staying: [], events: [], rooms_busy: new Set(), blocks_by_src: {}, externals: [] };
        if (!idx[iso].blocks_by_src) idx[iso].blocks_by_src = {};
        if (!idx[iso].externals) idx[iso].externals = [];
        if (e.kind === "block") {
          idx[iso].blocks_by_src[srcKey] = (idx[iso].blocks_by_src[srcKey] || 0) + 1;
        } else {
          idx[iso].rooms_busy.add(e.room_id);
        }
        idx[iso].externals.push({ ...e, _srcKey: srcKey });
        d.setDate(d.getDate() + 1);
      }
    }
    for (const e of guideEvents) add(e.date, "events", e);
    return idx;
  }, [bookings, externals, guideEvents]);

  const monthGrid = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [month]);

  // Filter: only bookings to show in calendar (active stays only — never show cancelled/rejected/frozen/no-show)
  // checked_in is treated as confirmed (guest is in-house); auto-promoted to "completed" once check_out is past.
  const visibleBookings = useMemo(() => {
    const todayISO = ISO(new Date());
    const internal = bookings
      .filter((b) => ["pending", "confirmed", "checked_in", "completed"].includes(b.status))
      .map((b) => {
        // Auto-promote past stays to "completed" for visual purposes (don't mutate DB)
        if (b.check_out && b.check_out <= todayISO && b.status !== "completed") {
          return { ...b, _displayStatus: "completed" };
        }
        if (b.status === "checked_in") {
          return { ...b, _displayStatus: "confirmed" };
        }
        return { ...b, _displayStatus: b.status };
      });
    // Convert external (OTA iCal) bookings into pseudo-bookings so the same
    // bar-rendering logic renders them. Only events with kind="booking"
    // become guest-style bars; kind="block" entries (host-set blocks on the
    // channel — they're NOT real reservations) are filtered out here and
    // rendered separately as a thin gray "Blocco" stripe via `externalBlocks`.
    const ext = (externals || [])
      .filter((e) => e.kind !== "block")
      // iter124: hide externals already covered by an internal booking on the
      // same room+dates (these are the OTA iCal mirror of an already-merged
      // reservation — showing them would create the phantom CL... bars the
      // host saw on the calendar).
      .filter((e) => !e.shadowed_by_internal)
      .map((e) => {
        const srcRaw = String(e.source || "").toLowerCase();
        const srcKey = srcRaw.includes("airbnb")
          ? "airbnb"
          : (srcRaw.includes("booking") ? "booking" : "external");
        const label = srcKey === "airbnb" ? "Airbnb" : (srcKey === "booking" ? "Booking.com" : (e.source || "External"));
        return {
          id: `ext-${e.id || e.uid}`,
          code: e.uid ? String(e.uid).split("-")[0].slice(0, 8) : "OTA",
          room_id: e.room_id,
          room_name: e.room_name || "Camera",
          guest: { first_name: label, last_name: e.summary || label },
          guests: 1,
          check_in: e.start,
          check_out: e.end,
          status: "confirmed",
          _displayStatus: "confirmed",
          source: srcKey,
          _external: true,
          _externalSummary: e.summary,
          _externalUrl: e.url,
        };
      });
    return [...internal, ...ext];
  }, [bookings, externals]);

  // Split flat grid into weeks (rows of 7) for Google-Calendar-style continuous bars
  const weeks = useMemo(() => {
    const out = [];
    for (let i = 0; i < monthGrid.length; i += 7) out.push(monthGrid.slice(i, i + 7));
    return out;
  }, [monthGrid]);

  // For each week, compute booking BARS that span their visible date range in that week.
  // Each bar gets {booking, startCol (0-6), span (1-7), continuesLeft, continuesRight, row}
  const weekBars = useMemo(() => {
    return weeks.map((days) => {
      const weekStartISO = ISO(days[0]);
      const weekEndISO = ISO(days[6]);
      const visible = visibleBookings.filter((b) =>
        b.check_in <= weekEndISO && b.check_out > weekStartISO
      );
      // Sort by check-in date then by check_out for stable rows
      visible.sort((a, b) => (a.check_in.localeCompare(b.check_in) || a.check_out.localeCompare(b.check_out)));
      const bars = [];
      // Greedy row assignment: each bar gets the lowest row index that doesn't collide
      const rowOccupancy = []; // rowOccupancy[row] = bitmask of cols 0-6
      for (const b of visible) {
        const ciDate = b.check_in > weekStartISO ? b.check_in : weekStartISO;
        // Check-out is exclusive — bar ENDS the day before check_out.
        // To make it visually clear that the guest occupies up to and INCLUDING (check_out - 1),
        // we visualize from check_in to check_out-1 inclusive.
        const lastNightDate = new Date(b.check_out);
        lastNightDate.setDate(lastNightDate.getDate() - 1);
        const lastNightISO = ISO(lastNightDate);
        const coDate = lastNightISO < weekEndISO ? lastNightISO : weekEndISO;
        if (ciDate > coDate) continue;
        const startCol = days.findIndex((d) => ISO(d) === ciDate);
        const endCol = days.findIndex((d) => ISO(d) === coDate);
        if (startCol < 0 || endCol < 0) continue;
        const span = endCol - startCol + 1;
        const mask = ((1 << span) - 1) << startCol;
        let row = 0;
        while (rowOccupancy[row] !== undefined && (rowOccupancy[row] & mask) !== 0) row++;
        if (rowOccupancy[row] === undefined) rowOccupancy[row] = 0;
        rowOccupancy[row] |= mask;
        bars.push({
          b, startCol, span, row,
          continuesLeft: b.check_in < weekStartISO,
          continuesRight: lastNightISO > weekEndISO,
          isCheckIn: b.check_in >= weekStartISO && b.check_in <= weekEndISO,
          isCheckOut: b.check_out > weekStartISO && b.check_out <= weekEndISO && b.check_out > b.check_in,
        });
      }
      return { days, bars, maxRow: rowOccupancy.length };
    });
  }, [weeks, bookings]);

  const totalRooms = rooms.length || 0;
  const selectedInfo = dayIndex[selectedDate] || { checkins: [], checkouts: [], staying: [], events: [], rooms_busy: new Set(), blocks_by_src: {}, externals: [] };
  // iter232 — dettaglio di DOMANI per la sezione "Check-out di domani",
  // utile per programmare la sera le pulizie del giorno dopo.
  const tomorrowIso = useMemo(() => {
    if (!selectedDate) return null;
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    return ISO(d);
  }, [selectedDate]);
  const tomorrowInfo = tomorrowIso ? (dayIndex[tomorrowIso] || { checkins: [], checkouts: [], staying: [], events: [], rooms_busy: new Set(), blocks_by_src: {}, externals: [] }) : null;

  const openNewBooking = (iso) => {
    setNewDate(iso);
    setNewOpen(true);
  };

  // iter186 — Auto-open NewBookingDialog quando arriviamo dal tab
  // "iCal in arrivo" via bottone "Crea manualmente". Il prefill contiene
  // room_id + check_in + check_out + ical_uid; passiamo tutto al dialog.
  const [prefill, setPrefill] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("icalPrefill");
      if (raw) {
        const data = JSON.parse(raw);
        sessionStorage.removeItem("icalPrefill");
        if (data?.check_in) {
          setSelectedDate(data.check_in);
          setNewDate(data.check_in);
          setPrefill(data);
          setNewOpen(true);
        }
      }
    } catch { /* noop */ }
    // Only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="calendar-tab">
      {/* Walk-in dall'App Guida senza camera — non appaiono nei calendari finché non viene assegnata una stanza */}
      <UnassignedWalkinsBanner />
      {/* Banner Duplicati: due o più prenotazioni con stessa camera + date + cognome */}
      {duplicateGroups.length > 0 && !dupDismissed && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-xl p-4 space-y-3 relative"
          data-testid="duplicates-banner"
        >
          {/* iter200 — X per chiudere il banner (7gg, riappare se cambia il conteggio) */}
          <button
            type="button"
            onClick={dismissDup}
            className="absolute top-2 right-2 p-1 rounded hover:bg-amber-200 text-amber-700 hover:text-amber-900 transition-colors z-10"
            aria-label="Chiudi avviso per 7 giorni"
            title="Ho controllato, chiudi per 7 giorni"
            data-testid="duplicates-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-2 pr-8">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">
                {duplicateGroups.length} {duplicateGroups.length === 1 ? "duplicato rilevato" : "duplicati rilevati"} fra le prenotazioni
              </div>
              <div className="text-xs text-amber-800">
                Stessa camera, stesse date e stesso cognome — probabilmente lo stesso ospite è stato registrato più volte.
                Clicca "Risolvi tutti automaticamente" per unirli in un'unica prenotazione (viene tenuto il record con più dati).
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => autoMergeAll(false)}
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              data-testid="auto-merge-all-btn"
            >
              <Link2 className="w-3.5 h-3.5 mr-1.5" />
              Risolvi tutti automaticamente
            </Button>
          </div>
          {duplicateGroups.some((g) => g.cross_room) && (
            <div className="flex items-center justify-between gap-2 bg-orange-50 border border-orange-200 rounded p-2 text-xs">
              <span className="text-orange-900">
                ⚠️ {duplicateGroups.filter((g) => g.cross_room).length} gruppi hanno camere DIVERSE — saltati per sicurezza.
                Potrebbero essere veri duplicati con room_id non assegnato.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => autoMergeAll(true)}
                className="text-orange-700 border-orange-300 shrink-0"
                data-testid="auto-merge-force-btn"
              >
                Forza unione anche camere diverse
              </Button>
            </div>
          )}
          <div className="space-y-2">
            {duplicateGroups.map((g, gi) => (
              <div
                key={`${g.room_id}-${g.check_in}-${g.last_name}-${gi}`}
                className="bg-white border border-amber-200 rounded-lg p-3 space-y-2"
                data-testid={`duplicate-group-${gi}`}
              >
                <div className="text-xs text-[#635F59] flex items-center justify-between gap-2">
                  <span>
                    <b className="text-[#1E3A5F] capitalize">{g.last_name}</b> ·
                    {" "}{formatDate(g.check_in)} → {formatDate(g.check_out)} ·
                    {" "}<i>{_str(g.bookings?.[0]?.room_name)}</i>
                    {g.cross_room && (
                      <span className="ml-2 inline-block bg-orange-100 text-orange-800 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase">
                        Camere diverse
                      </span>
                    )}
                    {g.mixed_reason === "mixed_ical_ota" && (
                      <span
                        className="ml-2 inline-block bg-blue-100 text-blue-800 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase"
                        title="Un record è uno stub iCal anonimo di Booking.com, l'altro è l'XLS/email con dati completi. Consigliamo di tenere l'XLS/email."
                      >
                        iCal + OTA
                      </span>
                    )}
                  </span>
                  <span className="text-amber-700 font-semibold">{g.count || g.bookings?.length} record</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {(g.bookings || []).map((b) => (
                    <div
                      key={b.code}
                      className={`border rounded p-2 text-xs space-y-1 ${g.suggested_winner === b.code ? "border-emerald-400 bg-emerald-50" : "border-[#E8E2D9]"}`}
                      data-testid={`duplicate-card-${b.code}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[#AC5D45] font-bold">{b.code}</span>
                        <div className="flex items-center gap-1">
                          {g.suggested_winner === b.code && (
                            <span
                              className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded font-bold uppercase tracking-wide"
                              title="Sistema consiglia di tenere questo record: contiene nome ospite e dati OTA reali"
                              data-testid={`suggested-winner-${b.code}`}
                            >
                              Consigliato
                            </span>
                          )}
                          <span className="text-[10px] text-[#635F59] uppercase">{b.source || "—"}</span>
                        </div>
                      </div>
                      <div>{b.guest?.first_name} {b.guest?.last_name}</div>
                      {b.guest?.phone && <div><Phone className="w-3 h-3 inline mr-1" />{b.guest.phone}</div>}
                      {b.guest?.email && <div><Mail className="w-3 h-3 inline mr-1" />{b.guest.email}</div>}
                      <div className="text-[#635F59]">
                        €{Number(b.total || 0).toFixed(2)}
                        {b.total_pending_ota_email && (
                          <span
                            className="ml-1 px-1 py-0 rounded bg-amber-100 text-amber-800 text-[9px] font-bold tracking-wide align-middle"
                            title="Prezzo stimato da tariffa base — il totale reale verrà aggiornato quando arriva l'email di conferma da Booking.com"
                          >
                            STIMA
                          </span>
                        )}
                      </div>
                      {(g.bookings || []).length >= 2 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[11px] h-7 w-full mt-1"
                          onClick={() => {
                            // Merge all other records in this group into this one
                            const others = (g.bookings || []).filter((x) => x.code !== b.code);
                            (async () => {
                              for (const o of others) {
                                try {
                                  await api.post("/admin/bookings/merge-duplicate", { winner_code: b.code, loser_code: o.code });
                                } catch (e) {
                                  toast.error(`Errore merge ${o.code}: ${errMsg(e)}`);
                                }
                              }
                              toast.success(`✓ Tenuto ${b.code}, uniti ${others.length} record`);
                              await load();
                            })();
                          }}
                          data-testid={`merge-keep-${b.code}`}
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          Tieni questo
                        </Button>
                      )}
                      {/* iter169: quick actions — open in bookings list OR delete
                          this specific record (host asked: "mi permetti di
                          cancellare o collegarmi a elenco prenotazioni") */}
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[11px] h-7"
                          onClick={() => navigate(`/admin/dashboard?tab=bookings&code=${encodeURIComponent(b.code)}`)}
                          data-testid={`dup-open-${b.code}`}
                          title="Apri questa prenotazione nell'elenco per modifiche avanzate"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Apri
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-[11px] h-7 border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={async () => {
                            if (!b.id) {
                              toast.error("ID prenotazione non disponibile");
                              return;
                            }
                            if (!window.confirm(`Cancellare la prenotazione ${b.code} (${b.guest?.first_name || ""} ${b.guest?.last_name || ""})? Verrà spostata nel cestino per 7 giorni prima dell'eliminazione definitiva.`)) return;
                            try {
                              await api.delete(`/admin/bookings/${b.id}`);
                              toast.success(`✓ Prenotazione ${b.code} spostata nel cestino`);
                              await load();
                            } catch (e) {
                              toast.error(`Errore: ${errMsg(e)}`);
                            }
                          }}
                          data-testid={`dup-delete-${b.code}`}
                          title="Sposta questa singola prenotazione nel cestino (recuperabile 7gg)"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Cancella
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* HEADER */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
            <CalIcon className="w-5 h-5" /> Calendario
          </h3>
          <p className="text-xs text-[#635F59] mt-1">
            Vista mensile con prenotazioni e disponibilità. Tocca un giorno per vederne il dettaglio, clicca <b>+</b> per creare una nuova prenotazione.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={() => setMonth(subMonths(month, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-display text-lg text-[#1E3A5F] capitalize min-w-[160px] text-center">
            {format(month, "MMMM yyyy", { locale: itLocale })}
          </div>
          <Button variant="outline" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => { setMonth(startOfMonth(new Date())); setSelectedDate(ISO(new Date())); }} data-testid="cal-today">Oggi</Button>
          <Button variant="outline" size="icon" onClick={load} data-testid="cal-refresh"><RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await api.get("/admin/ical-config");
                setIcalRooms(r.data || []);
                setIcalDialogOpen(true);
              } catch {
                toast.error("Errore caricamento config iCal");
              }
            }}
            data-testid="cal-ical-btn"
            className="border-sky-300 text-sky-700 hover:bg-sky-50"
          >
            <Link2 className="w-4 h-4 mr-1" />iCal
          </Button>
          <Button onClick={() => openNewBooking(selectedDate)} data-testid="cal-new" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <Plus className="w-4 h-4 mr-1" />Nuova prenotazione
          </Button>
        </div>
      </div>

      {guideError && !guideDismissed && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 flex items-start gap-2 text-xs relative pr-9">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div><b>Guida Digitale non collegata:</b> {guideError}. Il calendario mostra comunque le prenotazioni interne.</div>
          {/* iter200 — X per chiudere l'avviso per 7 giorni */}
          <button
            type="button"
            onClick={dismissGuide}
            className="absolute top-2 right-2 p-1 rounded hover:bg-amber-200 text-amber-700 hover:text-amber-900 transition-colors"
            aria-label="Chiudi avviso per 7 giorni"
            title="Chiudi per 7 giorni"
            data-testid="guide-offline-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* MONTHLY GRID — Google-Calendar style with continuous bars per booking */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-[#635F59] bg-[#FDFBF7] border-b border-[#E8E2D9]">
          {WEEK_LABELS.map((w) => (<div key={w} className="py-2 text-center font-semibold">{w}</div>))}
        </div>
        <div data-testid="cal-grid">
          {weekBars.map(({ days, bars, maxRow }, wIdx) => {
            // Each bar row is ~22px tall; reserve space below day-number for bars
            const BAR_ROW_H = 22;
            const BAR_TOP = 40; // px from top of cell where bar row 0 starts (leaves room for day number pinned top-left)
            const minCellH = Math.max(110, BAR_TOP + (maxRow + 1) * BAR_ROW_H + 8);
            return (
              <div key={`week-${days[0] ? ISO(days[0]) : wIdx}`} className="relative grid grid-cols-7 border-b border-[#E8E2D9] last:border-b-0">
                {/* Day cells (backdrop) */}
                {days.map((d) => {
                  const iso = ISO(d);
                  const info = dayIndex[iso] || { checkins: [], checkouts: [], staying: [], events: [], rooms_busy: new Set(), blocks_by_src: {}, externals: [] };
                  const isCurrent = isSameMonth(d, month);
                  const isSel = iso === selectedDate;
                  const isTd = isToday(d);
                  const todayISOstr = ISO(new Date());
                  const isPast = iso < todayISOstr;
                  const occupied = info.rooms_busy ? info.rooms_busy.size : 0;
                  const blocksBySrc = info.blocks_by_src || {};
                  const blocksAirbnb = blocksBySrc.airbnb || 0;
                  const blocksBooking = blocksBySrc.booking || 0;
                  return (
                    <button
                      key={iso}
                      type="button"
                      ref={isTd ? todayCellRef : null}
                      onClick={() => setSelectedDate(iso)}
                      onDoubleClick={() => openNewBooking(iso)}
                      data-testid={`cal-day-${iso}`}
                      style={{ minHeight: `${minCellH}px` }}
                      className={`relative border-r border-[#E8E2D9] last:border-r-0 p-2 text-left transition group
                        ${isPast && isCurrent ? "bg-gray-100" : isCurrent ? "bg-white" : "bg-[#FDFBF7] text-[#A8A6A2]"}
                        ${isSel ? "ring-2 ring-[#AC5D45] ring-inset z-20" : ""}
                        hover:bg-[#FDFBF7]`}
                    >
                      {/* Day number + occupancy badge pinned together to TOP-LEFT corner */}
                      <div className="absolute top-1.5 left-2 flex items-center gap-1.5 z-10">
                        <span
                          className={`text-sm ${
                            isTd
                              ? "bg-[#AC5D45] text-white rounded-full w-6 h-6 inline-flex items-center justify-center font-bold shadow"
                              : isPast && isCurrent
                              ? "text-gray-400"
                              : isCurrent
                              ? "text-[#1E3A5F] font-semibold"
                              : "text-[#A8A6A2]"
                          }`}
                        >
                          {format(d, "d")}
                        </span>
                        {totalRooms > 0 && occupied > 0 && (
                          <span
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                              occupied === totalRooms
                                ? "bg-rose-100 text-rose-700"
                                : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {occupied}/{totalRooms}
                          </span>
                        )}
                        {/* Note: OTA channel blocks (🔒A/🔒B) are intentionally
                            NOT shown here — this is the "Calendario Prenotazioni"
                            view and the admin only wants to see real guest
                            reservations. Blocks are visible in "Disponibilità
                            & Prezzi" page where they affect availability. */}
                      </div>
                      {/* "+" hover-only */}
                      {isCurrent && (
                        <span
                          role="button"
                          aria-label="Nuova prenotazione"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); openNewBooking(iso); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openNewBooking(iso); } }}
                          data-testid={`cal-plus-${iso}`}
                          className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-[#AC5D45] text-white text-xs items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer inline-flex z-10"
                        >+</span>
                      )}
                    </button>
                  );
                })}
                {/* Booking bars overlay — continuous across the week (layer above buttons) */}
                <div className="absolute inset-0 pointer-events-none z-30">
                  {bars.map(({ b, startCol, span, row, continuesLeft, continuesRight, isCheckIn, isCheckOut }) => {
                    const isWalkin = b.source === "walk_in";
                    const isExternal = b.source === "external" || b.source === "booking" || b.source === "airbnb";
                    const displayStatus = b._displayStatus || b.status;
                    // Visual tone follows DISPLAY status (auto-promotes finished stays to "completed")
                    const tone = displayStatus === "completed"
                      ? "bg-blue-300 hover:bg-blue-400 border-blue-500 text-blue-900"
                      : isWalkin
                      ? "bg-amber-300 hover:bg-amber-400 border-amber-500 text-amber-900"
                      : isExternal
                      ? "bg-sky-300 hover:bg-sky-400 border-sky-500 text-sky-900"
                      : displayStatus === "pending"
                      ? "bg-yellow-200 hover:bg-yellow-300 border-yellow-400 text-yellow-900"
                      : "bg-emerald-300 hover:bg-emerald-400 border-emerald-500 text-emerald-900";
                    const rl = continuesLeft ? "rounded-l-none" : "rounded-l-md";
                    const rr = continuesRight ? "rounded-r-none" : "rounded-r-md";
                    const surname = b.guest?.last_name || b.guest?.first_name || "—";
                    const roomLabel = _str(b.room_name) || "Cam";
                    const guests = b.guests || 1;
                    const roomShort = roomLabel.replace(/^Camera\s+/i, "").replace(/\s*\/.*$/, "").slice(0, 10);
                    // Primary label = physical room number from the Guide App (e.g. "#13"),
                    // falling back to a short room-type label if not assigned yet.
                    const hasRoomNumber = b.guide_room_number != null && b.guide_room_number !== "";
                    const extLabel = isExternal ? (b.source === "airbnb" ? "Airbnb" : (b.source === "booking" ? "Booking" : "OTA")) : null;
                    const primaryLabel = isExternal ? extLabel : (hasRoomNumber ? `#${b.guide_room_number}` : roomShort);
                    const handleBarClick = (e) => {
                      e.stopPropagation();
                      if (b._external) {
                        // iter200 — Naviga al menu iCal Inbox.
                        // iter201 — Passa l'UID così l'entry viene
                        // scrollata + evidenziata automaticamente,
                        // senza doverla cercare tra 20 righe.
                        const uid = b.uid ? `&uid=${encodeURIComponent(b.uid)}` : "";
                        navigate(`/admin/dashboard?tab=ical-inbox${uid}`);
                        return;
                      }
                      onOpenBooking && onOpenBooking(b);
                    };
                    return (
                      <div
                        key={`bar-${b.id}-w${wIdx}`}
                        role="button"
                        tabIndex={0}
                        onClick={handleBarClick}
                        onKeyDown={(e) => { if (e.key === "Enter") handleBarClick(e); }}
                        className={`absolute border ${tone} ${rl} ${rr} px-1 md:px-2 text-[10px] md:text-[11px] font-semibold flex items-center gap-0.5 md:gap-1.5 overflow-hidden whitespace-nowrap pointer-events-auto cursor-pointer shadow-sm transition`}
                        style={{
                          top: `${BAR_TOP + row * BAR_ROW_H}px`,
                          left: `calc(${(startCol / 7) * 100}% + 4px)`,
                          width: `calc(${(span / 7) * 100}% - 8px)`,
                          height: `${BAR_ROW_H - 4}px`,
                        }}
                        title={isExternal
                          ? `${extLabel} · ${roomLabel} · ${formatDate(b.check_in)} → ${formatDate(b.check_out)}${b._externalSummary ? ` · ${b._externalSummary}` : ""}`
                          : `${hasRoomNumber ? `Camera #${b.guide_room_number} · ` : ""}${roomLabel} · ${b.guest?.first_name || ""} ${surname} · ${guests} ospite${guests > 1 ? "i" : ""} · ${formatDate(b.check_in)} → ${formatDate(b.check_out)}${isWalkin ? " · WALK-IN" : ""}${displayStatus === "completed" ? " · COMPLETATA" : ""} — click per aprire`}
                        data-testid={`bar-${b.id}-w${wIdx}`}
                      >
                        {isCheckIn && !continuesLeft && (
                          <span className="hidden md:inline-flex shrink-0">
                            {isWalkin ? <UserPlus className="w-2.5 h-2.5 opacity-80" /> : <ChevronRight className="w-2.5 h-2.5 opacity-80" />}
                          </span>
                        )}
                        <BookingPill
                          booking={b}
                          className="truncate flex-1 min-w-0"
                          data-testid={`pill-${b.id}-w${wIdx}`}
                        />
                        {isCheckOut && !continuesRight && (
                          <span className="hidden md:inline-flex ml-auto shrink-0">
                            <CornerUpLeft className="w-2.5 h-2.5 opacity-80" />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* LEGEND — stile pillole come Prenotazioni, solo gli stati visibili nel calendario */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-[#E8E2D9] rounded-lg px-3 py-2">
        <BookingStatusLegend statuses={["pending", "confirmed", "completed"]} className="!bg-transparent !border-0 !px-0 !py-0" />
        <div className="flex flex-wrap items-center gap-2 text-[10px] text-[#635F59] border-l border-[#E8E2D9] pl-3 ml-1" data-testid="cal-legend-sources">
          <span className="font-semibold text-[#1E3A5F]">Fonte:</span>
          <span className="inline-flex items-center gap-0.5"><b className="text-rose-600 font-extrabold">A</b> Air BnB</span>
          <span className="inline-flex items-center gap-0.5"><b className="text-sky-600 font-extrabold">B</b> Booking</span>
          <span className="inline-flex items-center gap-0.5"><b className="text-[#1A1A1A] font-extrabold">S</b> sito</span>
          <span className="inline-flex items-center gap-0.5"><b className="text-orange-600 font-extrabold">W</b> walk</span>
          <span className="text-[#635F59]/70 italic">· es. <span className="font-mono">n13 B Saulle · 2p</span></span>
        </div>
      </div>

      {/* DAY DETAIL */}
      <DayDetail
        iso={selectedDate}
        info={selectedInfo}
        tomorrowIso={tomorrowIso}
        tomorrowInfo={tomorrowInfo}
        totalRooms={totalRooms}
        hideOtaBlocks={hideOtaBlocks}
        onToggleHideBlocks={toggleHideBlocks}
        onNew={() => openNewBooking(selectedDate)}
      />

      {/* NEW BOOKING DIALOG */}
      <NewBookingDialog
        open={newOpen}
        onOpenChange={(open) => { setNewOpen(open); if (!open) setPrefill(null); }}
        date={newDate}
        rooms={rooms}
        prefill={prefill}
        onCreated={() => { setNewOpen(false); setPrefill(null); load(); }}
      />

      {/* iCal SYNC DIALOG */}
      <IcalConfigDialog
        open={icalDialogOpen}
        onOpenChange={setIcalDialogOpen}
        rooms={icalRooms}
        onSaved={async () => {
          try {
            const r = await api.get("/admin/ical-config");
            setIcalRooms(r.data || []);
          } catch { /* ignore */ }
        }}
      />
    </div>
  );
}

// --------------- Day detail ---------------
const DayDetail = ({ iso, info, tomorrowIso, tomorrowInfo, totalRooms, hideOtaBlocks, onToggleHideBlocks, onNew }) => {
  const occupied = info.rooms_busy ? info.rooms_busy.size : 0;
  const free = Math.max(0, totalRooms - occupied);
  const human = format(parseISO(iso), "EEEE d MMMM yyyy", { locale: itLocale });
  const tomorrowHuman = tomorrowIso ? format(parseISO(tomorrowIso), "EEEE d MMMM", { locale: itLocale }) : "";
  // iter232 — Stessa logica del widget "Oggi" (AdminWidgetPage):
  //  · "Check-in oggi" = SOLO chi deve ancora arrivare (status ≠ checked_in).
  //    Se l'ospite ha già fatto check-in via App Guida, non appare qui.
  //  · "In casa (adesso)" = chi effettivamente dorme stanotte:
  //    - `info.staying` (già in casa da giorni precedenti)
  //    - + `info.checkins` filtrato per status === "checked_in"
  //    Chi ha check-in oggi ma NON si è ancora presentato NON è in casa.
  const pendingCheckins = (info.checkins || []).filter((b) => b.status !== "checked_in");
  const alreadyCheckedInToday = (info.checkins || []).filter((b) => b.status === "checked_in");
  const inHouseNow = [...(info.staying || []), ...alreadyCheckedInToday];
  // Apply user filter on OTA externals:
  //  - Always hide blocks here (this is the "Prenotazioni Clienti" view —
  //    blocks belong to "Disponibilità & Prezzi" page only).
  //  - iter124: hide rows shadowed by an internal booking on same room+dates.
  const externalsToShow = (info.externals || [])
    .filter((e) => e.kind !== "block")
    .filter((e) => !e.shadowed_by_internal);
  const tomorrowCheckouts = (tomorrowInfo?.checkouts || []);
  const hiddenCount = 0;
  return (
    <div className="bg-white border border-[#E8E2D9] rounded-xl p-5" data-testid="cal-day-detail">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <div className="font-display text-xl text-[#1E3A5F] capitalize">{human}</div>
          <div className="text-xs text-[#635F59] mt-0.5 flex gap-2 flex-wrap">
            <span><b className="text-emerald-700">{info.checkins.length}</b> check-in</span>
            <span>·</span>
            <span><b className="text-sky-700">{info.checkouts.length}</b> check-out</span>
            {totalRooms > 0 && (<>
              <span>·</span>
              <span><b className="text-[#AC5D45]">{occupied}/{totalRooms}</b> occupate</span>
              <span>·</span>
              <span className={`${free === 0 ? "text-rose-600 font-semibold" : "text-emerald-700"}`}>{free} libere</span>
            </>)}
          </div>
        </div>
        <Button onClick={onNew} variant="outline" data-testid="day-new-booking" className="border-[#AC5D45] text-[#AC5D45]">
          <Plus className="w-4 h-4 mr-1" />Nuova qui
        </Button>
      </div>

      {pendingCheckins.length > 0 && <Section icon={LogIn} title={`Check-in oggi (${pendingCheckins.length})`} color="text-emerald-700" defaultOpen={true}>{pendingCheckins.map((b) => <BookingRow key={`ci-${b.id}`} b={b} />)}</Section>}
      {inHouseNow.length > 0 && <Section icon={BedDouble} title={`In casa stanotte (${inHouseNow.length})`} color="text-[#AC5D45]" defaultOpen={false}>{inHouseNow.map((b) => <BookingRow key={`st-${b.id}`} b={b} />)}</Section>}
      {info.checkouts.length > 0 && <Section icon={LogOut} title={`Check-out oggi (${info.checkouts.length})`} color="text-sky-700" defaultOpen={true}>{info.checkouts.map((b) => <BookingRow key={`co-${b.id}`} b={b} />)}</Section>}
      {tomorrowCheckouts.length > 0 && (
        <Section
          icon={LogOut}
          title={`Check-out di domani (${tomorrowCheckouts.length})`}
          color="text-orange-700"
          defaultOpen={false}
        >
          <div className="text-[10px] uppercase tracking-wider text-[#635F59] mb-1">
            {tomorrowHuman}
          </div>
          {tomorrowCheckouts.map((b) => <BookingRow key={`cot-${b.id}`} b={b} />)}
        </Section>
      )}
      {externalsToShow.length > 0 && (
        <Section
          icon={ExternalLink}
          title={`Prenotazioni OTA (Airbnb/Booking) — ${externalsToShow.length}`}
          color="text-sky-700"
          defaultOpen={false}
        >
          {externalsToShow.map((e, i) => <ExternalRow key={`ext-${e.id || e.uid || i}`} e={e} />)}
        </Section>
      )}
      {info.events.length > 0 && <Section icon={Compass} title={`Eventi guida (${info.events.length})`} color="text-violet-700" defaultOpen={false}>{info.events.map((e, i) => <EventRow key={`${e.date || ""}-${e.title || ""}-${i}`} e={e} />)}</Section>}
      {info.checkins.length === 0 && info.checkouts.length === 0 && info.staying.length === 0 && info.events.length === 0 && externalsToShow.length === 0 && tomorrowCheckouts.length === 0 && (
        <div className="text-center text-sm text-[#635F59] py-6">Nessuna prenotazione o evento.</div>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, title, color, action, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-4 first:mt-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-1.5 mb-2 font-display font-bold text-base tracking-tight ${color}`}
      >
        <Icon className="w-5 h-5" />
        <span>{title}</span>
        {action && <span className="ml-auto" onClick={(e) => e.stopPropagation()}>{action}</span>}
        {open
          ? <ChevronUp className={`w-5 h-5 ${action ? "" : "ml-auto"}`} strokeWidth={2.5} />
          : <ChevronDown className={`w-5 h-5 ${action ? "" : "ml-auto"}`} strokeWidth={2.5} />
        }
      </button>
      {open && <div className="space-y-2">{children}</div>}
    </div>
  );
};

/* Compact inline toggle that lets the host override the parking slot
   assigned to a booking. The customer chooses garage vs outdoor on the
   public form; if the requested slot is full the host can swap it here.
   The change calls PATCH /api/admin/bookings/{id}/parking-type which
   also re-syncs the Guide App so its parking dashboard stays accurate. */
/* Inline "Modifica totale" button: opens a prompt so the host can enter
   the real Booking.com / Airbnb / Airbnb total for an imported booking
   (or correct any total). Calls PATCH /admin/bookings/{id}/total which
   also re-computes the deposit (30% for deposit_30, 100% for non_refundable). */
const EditTotalButton = ({ booking }) => {
  const [saving, setSaving] = useState(false);
  const handleClick = async (e) => {
    e.stopPropagation();
    if (saving) return;
    const current = Number(booking.total || 0).toFixed(2);
    const input = window.prompt(
      `Inserisci il totale corretto della prenotazione (Booking.com / Airbnb).\nValore attuale: €${current}\n\nUsa il punto o la virgola come separatore decimale (es. 207.10 o 207,10).`,
      current,
    );
    if (input === null) return;
    const cleaned = String(input).trim().replace(",", ".");
    const newTotal = Number(cleaned);
    if (!isFinite(newTotal) || newTotal < 0) {
      toast.error("Importo non valido");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/admin/bookings/${booking.id}/total`, { total: newTotal });
      toast.success(`Totale aggiornato → €${res.data.total.toFixed(2)}`);
      // Hard refresh to pick up the new total/deposit/badge state.
      // CalendarTab caches bookings in state; mutating the prop in place
      // would violate react-hooks immutability rules and not trigger a
      // re-render anyway. A reload is fine for an admin-only action.
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      toast.error(errMsg(err, "Errore aggiornamento totale"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={saving}
      data-testid={`edit-total-${booking.code}`}
      className="text-[10px] text-[#AC5D45] underline hover:text-[#8A4936] font-normal whitespace-nowrap"
      title="Modifica il totale (es. per allineare al prezzo reale di Booking.com)"
    >
      ✏️ Modifica
    </button>
  );
};

const ParkingTypeToggle = ({ bookingId, bookingCode, initial }) => {  const [val, setVal] = useState(initial || "garage");
  const [saving, setSaving] = useState(false);
  const setType = async (next) => {
    if (next === val || saving) return;
    setSaving(true);
    const prev = val;
    setVal(next);  // optimistic
    try {
      await api.patch(`/admin/bookings/${bookingId}/parking-type`, { parking_type: next });
      toast.success(next === "garage" ? "Parcheggio aggiornato → Garage" : "Parcheggio aggiornato → Esterno");
    } catch (e) {
      setVal(prev);
      toast.error(errMsg(e, "Errore nel cambio parcheggio"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="bg-white border border-[#E8E2D9] rounded p-2.5"
      data-testid={`parking-override-${bookingCode}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-[#635F59] mb-1.5 font-bold">
        🅿️ Tipo parcheggio (modifica se serve)
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onClick={() => setType("garage")}
          disabled={saving}
          data-testid={`parking-set-garage-${bookingCode}`}
          className={`text-xs px-2 py-1.5 rounded border transition ${
            val === "garage"
              ? "bg-[#AC5D45] text-white border-[#AC5D45] font-bold"
              : "bg-[#FDFBF7] text-[#635F59] border-[#E8E2D9] hover:border-[#AC5D45]"
          }`}
        >
          🏠 Garage / Custodito
        </button>
        <button
          type="button"
          onClick={() => setType("outdoor")}
          disabled={saving}
          data-testid={`parking-set-outdoor-${bookingCode}`}
          className={`text-xs px-2 py-1.5 rounded border transition ${
            val === "outdoor"
              ? "bg-[#1E3A5F] text-white border-[#1E3A5F] font-bold"
              : "bg-[#FDFBF7] text-[#635F59] border-[#E8E2D9] hover:border-[#1E3A5F]"
          }`}
        >
          🌳 Esterno / Custodito
        </button>
      </div>
      <div className="text-[10px] text-[#635F59] mt-1 italic">
        La scelta è sincronizzata automaticamente con la Guide App.
      </div>
    </div>
  );
};

const BookingRow = ({ b }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const g = b.guest || {};
  const items = buildExtrasList(b);
  const todayISO = format(new Date(), "yyyy-MM-dd");
  // Only mark "Check-in fatto" when the host (or the Guide app) has
  // EXPLICITLY marked the booking as `checked_in` / `completed`. A merely
  // `confirmed` status with check_in <= today is NOT a real check-in — it's
  // just the reservation date already arrived.
  const checkedIn = ["checked_in", "completed"].includes(b.status);
  const fullyPaid = b.status === "completed" || b.rate_type === "non_refundable";
  const depositPaid = b.status === "confirmed" && b.rate_type === "deposit_30";
  const isPending = b.status === "pending";
  const pm = b.payment_method;

  return (
    <div className="bg-white border border-[#E8E2D9] rounded-lg overflow-hidden hover:shadow-sm transition" data-testid={`day-bk-${b.code}`}>
      {/* HEADER (click to expand) */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 text-left hover:bg-[#FDFBF7] transition"
        data-testid={`day-bk-toggle-${b.code}`}
      >
        {/* Status badges row */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          {checkedIn && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1" variant="outline">
              <CheckCircle2 className="w-3 h-3" />Check-in fatto
            </Badge>
          )}
          {fullyPaid && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1" variant="outline">
              <CheckCircle2 className="w-3 h-3" />Pagato
            </Badge>
          )}
          {depositPaid && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 gap-1" variant="outline">
              <Clock className="w-3 h-3" />Acconto versato
            </Badge>
          )}
          {isPending && (
            <Badge className="bg-amber-50 text-amber-700 border-amber-300 gap-1" variant="outline">
              <Clock className="w-3 h-3" />In attesa
            </Badge>
          )}
          {b.is_returning_guest && (
            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">★ Habitué</Badge>
          )}
          {/* iter173: booking code is now a clickable link → apre elenco
              prenotazioni con questo codice pre-filtrato. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/dashboard?tab=bookings&code=${encodeURIComponent(b.code)}`); }}
            className="font-mono text-[11px] text-[#AC5D45] font-semibold ml-auto hover:underline"
            data-testid={`day-bk-openlist-${b.code}`}
            title="Apri questa prenotazione nell'elenco prenotazioni"
          >
            {b.code}
          </button>
        </div>

        {/* Main info */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-semibold text-[#1E3A5F] text-base">{g.first_name} {g.last_name}</span>
          <span className="text-[#635F59] inline-flex items-center gap-1 text-xs">
            <BedDouble className="w-3.5 h-3.5" />
            {b.guide_room_number != null && b.guide_room_number !== "" && (
              <b className="text-[#AC5D45]">Camera n{b.guide_room_number}</b>
            )}
            {b.guide_room_number != null && b.guide_room_number !== "" && <span>·</span>}
            {_str(b.room_name)}
          </span>
          <span className="text-[#635F59] inline-flex items-center gap-1 text-xs">
            <UsersIcon className="w-3.5 h-3.5" /> {b.guests} {b.guests === 1 ? "ospite" : "ospiti"}
          </span>
          <span className="text-[#635F59] text-xs">
            {formatDate(b.check_in)} → {formatDate(b.check_out)} · <b>{b.nights}n</b>
          </span>
          {items.length > 0 && (
            <Badge variant="outline" className="bg-[#FDF1E6] text-[#AC5D45] border-[#AC5D45]/30 gap-1">
              <Sparkles className="w-3 h-3" />{items.length} servizi extra
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </Badge>
          )}
          <span className="ml-auto font-display text-lg text-[#1E3A5F] inline-flex items-center gap-1.5">
            €{(b.total || 0).toFixed(2)}
            {b.total_pending_ota_email && (
              <span
                className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold tracking-wide"
                title="Prezzo stimato da tariffa base — il totale reale verrà aggiornato quando arriva l'email di conferma da Booking.com"
              >
                STIMA
              </span>
            )}
          </span>
        </div>

        {/* Contact row */}
        {(g.email || g.phone) && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-[#635F59] mt-1.5">
            {g.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{g.email}</span>}
            {g.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{g.phone}</span>}
          </div>
        )}
      </button>

      {/* iter173: Quick action bar — Apri (elenco) + Crea ricevuta collegata.
          Both use e.stopPropagation to not toggle the expand accordion. */}
      <div className="px-3 pb-2 -mt-1 flex flex-wrap items-center gap-1.5" data-testid={`day-bk-actions-${b.code}`}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(`/admin/dashboard?tab=bookings&code=${encodeURIComponent(b.code)}`); }}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[#E8E2D9] hover:border-[#AC5D45] hover:bg-[#FFF8F0] text-[#1E3A5F]"
          data-testid={`day-bk-open-${b.code}`}
        >
          <ExternalLink className="w-3 h-3" /> Apri
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/dashboard?tab=receipts-quotes&manual=1&q=${encodeURIComponent(b.code)}`);
          }}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold"
          data-testid={`day-bk-receipt-${b.code}`}
          title="Crea una ricevuta associata a questa prenotazione"
        >
          <ReceiptText className="w-3 h-3" /> Crea ricevuta
        </button>
      </div>

      {/* EXPANDED DETAILS */}
      {expanded && (
        <div className="border-t border-[#E8E2D9] bg-[#FDFBF7] p-3 space-y-3" data-testid={`day-bk-details-${b.code}`}>
          {/* EXTRAS LIST */}
          {items.length > 0 ? (
            <div>
              <h5 className="text-[11px] font-bold uppercase tracking-wider text-[#635F59] mb-1.5 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-[#AC5D45]" />
                {items.length} servizi extra
              </h5>
              <div className="space-y-1.5">
                {items.map((it, i) => {
                  const Icon = it.icon;
                  return (
                    <div
                      key={`${b.code}-${it.id || it.type || "x"}-${i}`}
                      className="flex items-start justify-between gap-2 bg-white border border-[#E8E2D9] rounded px-2.5 py-1.5"
                      data-testid={`day-bk-extra-${b.code}-${i}`}
                    >
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <Icon className="w-4 h-4 text-[#AC5D45] mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-[#1E3A5F]">{it.label}</div>
                          {it.detail && <div className="text-[10px] text-[#635F59]">{it.detail}</div>}
                        </div>
                      </div>
                      <div className="text-sm font-semibold shrink-0">
                        {it.free ? (
                          <span className="text-emerald-700 text-xs">Gratis</span>
                        ) : (
                          <span className="text-[#1E3A5F]">€{it.price.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-xs text-[#635F59] italic">Nessun servizio extra prenotato.</div>
          )}

          {/* PARKING TYPE OVERRIDE — visible only when guest requested a paid parking.
              Lets the host swap between Garage and Posto Esterno if the requested
              slot is full. The change is re-synced to the Guide App immediately so
              the parking dashboard stays accurate. */}
          {(() => {
            const choice = (b.extras || {}).parking_choice;
            const isPaidParking = ["paid_garage", "paid_outdoor", "paid_private", "paid_advance"].includes(choice);
            if (!isPaidParking) return null;
            const initial = b.parking_type
              || (choice === "paid_outdoor" ? "outdoor" : "garage");
            return (
              <ParkingTypeToggle
                bookingId={b.id}
                bookingCode={b.code}
                initial={initial}
              />
            );
          })()}

          {/* PRICE BREAKDOWN */}
          <div className="bg-white border border-[#E8E2D9] rounded p-2.5 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-[#635F59]">Soggiorno base</span><span>€{(b.base_total || 0).toFixed(2)}</span></div>
            {b.extras_total > 0 && (
              <div className="flex justify-between"><span className="text-[#635F59]">Extras</span><span>€{(b.extras_total || 0).toFixed(2)}</span></div>
            )}
            {b.discount > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Sconto early-bird</span><span>−€{(b.discount || 0).toFixed(2)}</span></div>
            )}
            {b.loyalty_discount > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Sconto fedeltà</span><span>−€{(b.loyalty_discount || 0).toFixed(2)}</span></div>
            )}
            {b.instagram_discount > 0 && (
              <div className="flex justify-between text-emerald-700"><span>Sconto Instagram</span><span>−€{(b.instagram_discount || 0).toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-bold pt-1 border-t border-[#E8E2D9] text-[#1E3A5F]">
              <span>Totale</span>
              <span className="inline-flex items-center gap-2">
                <span>€{(b.total || 0).toFixed(2)}</span>
                <EditTotalButton booking={b} />
              </span>
            </div>
            {b.deposit_amount > 0 && b.rate_type === "deposit_30" && (
              <div className="flex justify-between text-[#AC5D45] text-[11px]"><span>di cui acconto 30%</span><span>€{(b.deposit_amount || 0).toFixed(2)}</span></div>
            )}
            {/* Saldo da incassare — calcolato live (total − acconto già preso).
                Sempre visibile a colpo d'occhio: se è > 0 l'host sa subito
                quanto deve ancora chiedere al cliente. Non si mostra per le
                non-rimborsabili già pagate al 100% (saldo = 0). */}
            {(() => {
              const total = Number(b.total || 0);
              const deposit = Number(b.deposit_amount || 0);
              const saldo = Math.max(0, total - deposit);
              if (saldo <= 0.01) return null;
              return (
                <div
                  className="flex justify-between items-center font-bold text-[12px] bg-amber-50 border border-amber-300 rounded -mx-1 px-2 py-1.5 mt-1.5 text-amber-900"
                  data-testid={`day-bk-saldo-${b.code}`}
                  title="Importo che il cliente deve ancora versare"
                >
                  <span className="inline-flex items-center gap-1">💰 Saldo da incassare</span>
                  <span>€{saldo.toFixed(2)}</span>
                </div>
              );
            })()}
          </div>

          {/* PAYMENT METHOD + RATE TYPE */}
          <div className="flex flex-wrap items-center gap-2">
            {pm && (
              <Badge variant="outline" className={`gap-1 ${PAYMENT_TONE[pm] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                <Wallet className="w-3 h-3" />Metodo: <b>{PAYMENT_LABEL[pm] || pm}</b>
              </Badge>
            )}
            {b.rate_type && (
              <Badge variant="outline" className="bg-[#FDFBF7] text-[#635F59] border-[#E8E2D9] gap-1">
                <CreditCard className="w-3 h-3" />
                {b.rate_type === "non_refundable" ? "Non rimborsabile (pagato 100%)" : "Acconto 30% + saldo all'arrivo"}
              </Badge>
            )}
            <Badge variant="outline" className="border-[#E8E2D9]">
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${STATUS_DOT[b.status]}`} />
              {STATUS_LABEL[b.status] || b.status}
            </Badge>
            {b.source && (
              <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">
                Origine: {b.source}
              </Badge>
            )}
          </div>

          {/* TASSA DI SOGGIORNO + PRENOTAZIONE PAGATA (host manual tracking) */}
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[#E8E2D9]" data-testid={`day-bk-tassa-row-${b.code}`}>
            {b.tassa_paid ? (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Tassa soggiorno: <b>pagata</b>
                {b.tassa_amount ? <span className="font-normal">€{Number(b.tassa_amount).toFixed(2)}</span> : null}
                {b.tassa_method && <span className="font-normal">· {PAYMENT_LABEL[b.tassa_method] || b.tassa_method}</span>}
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-300 gap-1">
                <AlertCircle className="w-3 h-3" />
                Tassa soggiorno: <b>non pagata</b>
              </Badge>
            )}
            {b.tassa_note && (
              <span className="text-[10px] italic text-[#635F59]">📝 {b.tassa_note}</span>
            )}
            {b.booking_paid !== undefined && (
              b.booking_paid ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Saldo: <b>pagato</b>
                  {b.payment_method && <span className="font-normal">· {PAYMENT_LABEL[b.payment_method] || b.payment_method}</span>}
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 gap-1">
                  <Clock className="w-3 h-3" />
                  Saldo: <b>da incassare</b>
                </Badge>
              )
            )}
          </div>

          {/* NOTES */}
          {b.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs flex items-start gap-2">
              <StickyNote className="w-3.5 h-3.5 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-amber-900 italic">{b.notes}</div>
            </div>
          )}

          {/* RECEIPT LINK — click opens the same modal preview used in the
              bookings list, honouring admin JWT via api client */}
          {b.receipt?.filename && (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const res = await api.get(`/admin/bookings/${b.id}/receipt`, { responseType: "blob" });
                  const mime = b.receipt.mime || res.data.type || "application/octet-stream";
                  const blob = new Blob([res.data], { type: mime });
                  const url = URL.createObjectURL(blob);
                  setReceiptPreview({ url, mime, filename: b.receipt.filename });
                } catch {
                  toast.error("Impossibile aprire la ricevuta");
                }
              }}
              className="inline-flex items-center gap-1 text-xs text-[#AC5D45] hover:underline"
              data-testid={`day-bk-receipt-${b.code}`}
            >
              <Receipt className="w-3.5 h-3.5" />Ricevuta caricata: {b.receipt.filename}
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Receipt preview modal (day-preview level) — matches BookingReceiptsList popup */}
      {receiptPreview && (
        <div
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { URL.revokeObjectURL(receiptPreview.url); setReceiptPreview(null); }}
          data-testid={`day-bk-receipt-lightbox-${b.code}`}
        >
          <div
            className="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#E8E2D9] bg-[#FDFBF7]">
              <div className="text-sm text-[#1E3A5F] font-semibold truncate">
                {receiptPreview.filename || "Ricevuta"}
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={receiptPreview.url}
                  download={receiptPreview.filename || "ricevuta"}
                  className="text-xs text-[#AC5D45] hover:underline inline-flex items-center gap-1"
                  data-testid={`day-bk-receipt-download-${b.code}`}
                >
                  <Download className="w-3.5 h-3.5" /> Scarica
                </a>
                <button
                  type="button"
                  onClick={() => { URL.revokeObjectURL(receiptPreview.url); setReceiptPreview(null); }}
                  className="text-stone-500 hover:text-stone-800 text-xl leading-none"
                  aria-label="Chiudi"
                  data-testid={`day-bk-receipt-close-${b.code}`}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-stone-100 flex items-center justify-center">
              {receiptPreview.mime?.startsWith("image/") ? (
                <img src={receiptPreview.url} alt="ricevuta" className="max-w-full max-h-full" />
              ) : (
                <iframe src={receiptPreview.url} title="ricevuta" className="w-full h-[80vh] bg-white" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EventRow = ({ e }) => (
  <div className={`rounded-lg border p-3 text-sm ${EVENT_TONE[e.type] || EVENT_TONE.default}`}>
    <div className="font-semibold flex items-start justify-between gap-2">
      <span>{e.title}</span>
      {e.url && <a href={e.url} target="_blank" rel="noreferrer" className="text-[#AC5D45] hover:underline shrink-0"><ExternalLink className="w-3.5 h-3.5" /></a>}
    </div>
    <div className="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1">
      {e.time && <span>🕒 {e.time}</span>}
      {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
    </div>
  </div>
);

const ExternalRow = ({ e }) => {
  const navigate = useNavigate();
  const isBlock = e.kind === "block";
  const isAirbnb = e._srcKey === "airbnb";
  const tone = isBlock
    ? (isAirbnb ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-blue-50 border-blue-200 text-blue-900")
    : "bg-sky-50 border-sky-200 text-sky-900";
  const srcLabel = isAirbnb ? "Airbnb" : (e._srcKey === "booking" ? "Booking.com" : (e.source || "OTA"));
  const roomName = _str(e.room_name) || "—";
  const detail = isBlock
    ? "Date chiuse dal canale (non è una prenotazione)"
    : (e.summary || "Prenotazione sincronizzata");
  // iter200 — Click sulla riga (non sull'icona esterna) → menu iCal Inbox
  // dove l'host può trasformare la prenotazione OTA in una vera prenotazione.
  // Il piccolo icona ExternalLink in alto a destra RESTA come link diretto
  // al pannello OTA (Booking.com extranet / Airbnb host).
  const goToIcalInbox = (evt) => {
    evt.stopPropagation();
    if (isBlock) return; // i blocchi non si trasformano
    // iter201 — Deep-link con UID così l'entry giusta è evidenziata subito
    const uid = e.uid ? `&uid=${encodeURIComponent(e.uid)}` : "";
    navigate(`/admin/dashboard?tab=ical-inbox${uid}`);
  };
  return (
    <div
      className={`rounded-lg border p-3 text-sm ${tone} ${!isBlock ? "cursor-pointer hover:brightness-95 active:scale-[0.99] transition" : ""}`}
      data-testid={`day-ext-${e.id || e.uid}`}
      onClick={!isBlock ? goToIcalInbox : undefined}
      role={!isBlock ? "button" : undefined}
      tabIndex={!isBlock ? 0 : undefined}
      onKeyDown={!isBlock ? (evt) => { if (evt.key === "Enter") goToIcalInbox(evt); } : undefined}
      title={!isBlock ? "Apri in iCal Inbox per trasformare in prenotazione vera" : undefined}
    >
      <div className="font-semibold flex items-start justify-between gap-2">
        {e.url && !isBlock ? (
          <a
            href={e.url}
            target="_blank"
            rel="noreferrer"
            onClick={(evt) => evt.stopPropagation()}
            className="flex items-center gap-1.5 hover:underline"
            title={`Apri su ${srcLabel}`}
            data-testid={`day-ext-open-channel-${e.id || e.uid}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <b>{srcLabel}</b>
            <span className="text-xs font-normal opacity-80">· Prenotazione</span>
          </a>
        ) : (
          <span className="flex items-center gap-1.5">
            {isBlock ? "🔒" : <ExternalLink className="w-3.5 h-3.5" />}
            <b>{srcLabel}</b>
            <span className="text-xs font-normal opacity-80">· {isBlock ? "Blocco" : "Prenotazione"}</span>
          </span>
        )}
        {!isBlock && (
          <button
            type="button"
            onClick={goToIcalInbox}
            className="opacity-60 hover:opacity-100 shrink-0"
            title="Apri in iCal Inbox"
            data-testid={`day-ext-open-ical-${e.id || e.uid}`}
            aria-label="Apri in iCal Inbox"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="text-xs mt-1.5 space-y-0.5">
        <div><b>Camera:</b> {roomName}</div>
        <div><b>Periodo:</b> {e.start} → {e.end}</div>
        <div className="opacity-80"><b>Dettaglio iCal:</b> {detail}</div>
        {e.uid && <div className="opacity-50 text-[10px] font-mono truncate">UID: {e.uid}</div>}
      </div>
      {!isBlock && (
        <div className="mt-2 pt-2 border-t border-current/10 flex items-center justify-end gap-1 text-xs opacity-80">
          <span>Vai a iCal Inbox</span>
          <ChevronRight className="w-3.5 h-3.5" />
        </div>
      )}
    </div>
  );
};

// =================== Nuova Prenotazione Dialog ===================
const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", phone: "",
  room_id: "", guide_room_number: "", check_in: "", nights: 1, guests: 2,
  status: "confirmed", source: "walk_in", source_note: "", notes: "",
  // Tariffa: stessa logica della prenotazione pubblica.
  //  - "deposit_30"     → Standard (acconto 30% all'arrivo)
  //  - "non_refundable" → Non Rimborsabile (sconto −10%, pagamento subito)
  //  - "manual"         → Inserita a mano (nessuna formula applicata)
  rate_type: "manual",
  // Selezione di un Piano Tariffario configurato in admin (Standard /
  // Flessibile -21gg / Non rimborsabile -10% / Tariffa mensile -20% / …).
  // Quando l'host clicca una card piano, salviamo qui id+nome+discount così
  // il backend può applicare lo sconto e mostrare il nome nel doc.
  rate_plan_id: "",
  rate_plan_name: "",
  rate_discount_pct: 0,
  custom_nightly_rate: "", custom_total: "", discount_pct: "",
  // Manual tracking — host marks payment status from the booking detail
  booking_paid: false, payment_method: "",
  // Deposit/acconto — entered at creation time so the host doesn't have
  // to reopen the booking afterwards. In group bookings this lives on the
  // master (1/N) row only.
  deposit_amount: "", deposit_method: "",
  tassa_paid: false, tassa_method: "", tassa_amount: "", tassa_note: "",
  // "Ospite effettivo" override — host prenota a nome di X ma a dormire
  // viene Y. Stesso meccanismo della prenotazione pubblica (iter 124).
  // Visibile solo se l'host attiva il toggle qui sotto.
  booking_for_other: false,
  guest_for_first_name: "", guest_for_last_name: "", guest_for_email: "", guest_for_phone: "",
};

/**
 * AddExtraPersonsWidget (iter196 — 02/2026)
 * ------------------------------------------------------------------
 * Compatto: dentro il form Modifica Prenotazione. Consente di
 * aggiungere N persone (o extra) senza ricalcolare l'acconto —
 * l'importo va tutto nel saldo all'arrivo. Regola d'oro richiesta
 * dall'host: "l'acconto NON si ritocca dopo la prima conferma,
 * la differenza me la faccio a dare al saldo".
 *
 * Opzionale: rimanda l'email di conferma con i nuovi totali
 * (acconto invariato, saldo aggiornato) così il cliente vede la
 * ricevuta rifatta senza sensazione di dover ripagare l'acconto.
 */
function AddExtraPersonsWidget({ booking, onApplied }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(1);
  const [perNight, setPerNight] = useState("");
  const [note, setNote] = useState("Persona extra");
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState(false);

  const nights = Number(booking.nights || 0);
  const currentTotal = Number(booking.total || 0);
  const deposit = Number(booking.deposit_amount || 0);
  const perNightNum = Number(perNight || 0);
  const extra = Math.max(0, perNightNum * nights * Number(count || 0));
  const newTotal = currentTotal + extra;
  const newSaldo = Math.max(0, newTotal - deposit);

  const apply = async () => {
    if (!perNightNum || perNightNum < 0) {
      toast.error("Inserisci un prezzo a notte valido");
      return;
    }
    if (!count || count < 1) {
      toast.error("Numero persone deve essere ≥ 1");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/add-extras`, {
        count: Number(count),
        per_night: perNightNum,
        note: note || "Persona extra",
        resend_confirmation: resend,
      });
      onApplied?.(r.data?.booking || null);
      // Reset local form
      setCount(1);
      setPerNight("");
      setNote("Persona extra");
      setResend(false);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'aggiunta");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full mt-3 border-2 border-dashed border-[#AC5D45]/40 rounded-lg py-2.5 px-3 text-sm text-[#AC5D45] hover:bg-[#FFF8EF] transition inline-flex items-center justify-center gap-2"
        data-testid="add-extras-open"
      >
        <UserPlus className="w-4 h-4" />
        + Aggiungi persona o extra (senza ricalcolare l&apos;acconto)
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-[#AC5D45]/40 bg-[#FFF8EF] p-3 space-y-3" data-testid="add-extras-widget">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-[#1E3A5F] inline-flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-[#AC5D45]" /> Aggiungi persona / extra
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-stone-400 hover:text-stone-700 text-lg leading-none"
          aria-label="Chiudi"
          data-testid="add-extras-close"
        >
          ×
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <label className="block text-[#635F59] mb-1">N. persone</label>
          <Input
            type="number"
            min="1"
            step="1"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="h-8 text-sm"
            data-testid="add-extras-count"
          />
        </div>
        <div>
          <label className="block text-[#635F59] mb-1">€ / notte / persona</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="es. 30"
            value={perNight}
            onChange={(e) => setPerNight(e.target.value)}
            className="h-8 text-sm"
            data-testid="add-extras-per-night"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-[#635F59] mb-1">Descrizione (facoltativa)</label>
        <Input
          type="text"
          maxLength={60}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="h-8 text-sm"
          data-testid="add-extras-note"
        />
      </div>

      {/* Live preview */}
      <div className="rounded-md bg-white border border-[#E8E2D9] p-2.5 text-xs space-y-1" data-testid="add-extras-preview">
        <div className="flex justify-between text-[#635F59]">
          <span>Supplemento ({count}×{nights} notti × €{perNightNum.toFixed(2)})</span>
          <span className="font-semibold text-[#AC5D45]">+ €{extra.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Nuovo totale</span>
          <span className="font-semibold text-[#1E3A5F]">€{newTotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-emerald-800 bg-emerald-50 -mx-2.5 px-2.5 py-1 border-y border-emerald-200">
          <span>Acconto (invariato)</span>
          <span className="font-semibold">€{deposit.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-amber-800">
          <span>Saldo all&apos;arrivo</span>
          <span className="font-semibold">€{newSaldo.toFixed(2)}</span>
        </div>
      </div>

      <label className="flex items-start gap-2 text-xs text-[#635F59] cursor-pointer">
        <input
          type="checkbox"
          checked={resend}
          onChange={(e) => setResend(e.target.checked)}
          className="mt-0.5"
          data-testid="add-extras-resend"
        />
        <span>
          Rimanda l&apos;email di conferma al cliente con i nuovi totali (acconto invariato, saldo aggiornato).
        </span>
      </label>

      <div className="flex justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy} className="h-8 text-xs">
          Annulla
        </Button>
        <Button
          size="sm"
          onClick={apply}
          disabled={busy || extra <= 0}
          className="h-8 text-xs bg-[#AC5D45] hover:bg-[#8A4936] text-white"
          data-testid="add-extras-apply"
        >
          {busy ? "Applico…" : `Applica +€${extra.toFixed(2)}`}
        </Button>
      </div>
    </div>
  );
}


export const NewBookingDialog = ({ open, onOpenChange, date, rooms, onCreated, editingBooking = null, prefill = null }) => {
  const isEdit = !!editingBooking;
  const [form, setForm] = useState(EMPTY_FORM);
  const [smartSuggest, setSmartSuggest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extraRooms, setExtraRooms] = useState([]);
  // === EDIT MODE ONLY — "Aggiungi camera al gruppo" ===
  // Quando l'host apre una prenotazione ESISTENTE e vuole aggiungere una
  // camera al gruppo (senza dover ricreare tutto), questa mini-form
  // chiama `POST /admin/bookings/{code}/ensure-sibling` che clona le
  // date dal master, crea la sibling con room+guest indicati, e
  // ribilancia `group_size`/`group_total`/`group_index`. Attivata solo
  // in edit mode via il pulsante "+ Aggiungi camera al gruppo".
  const [addSiblingOpen, setAddSiblingOpen] = useState(false);
  const [addSiblingForm, setAddSiblingForm] = useState({
    room_id: "",
    guest_first_name: "",
    guest_last_name: "",
    guest_email: "",
    guests: "",
    guide_room_number: "",  // iter185: numero camera fisica al momento della creazione
    custom_total: "",       // iter186: prezzo camera (tariffa manuale) alla creazione
  });
  const [addingSibling, setAddingSibling] = useState(false);
  // Optional payment receipt image (base64) uploaded by the host directly
  // during booking creation. Kept in state and POSTed to
  // `/bookings/{code}/upload-receipt` right after the booking is saved so
  // the host doesn't have to re-open the booking just to attach the proof.
  const [receiptB64, setReceiptB64] = useState("");
  const [receiptName, setReceiptName] = useState("");
  // Dynamic rate plans loaded from /admin/rate-plans. When empty (e.g. while
  // the API is still loading or in an off-line dev environment) the UI
  // gracefully falls back to a "Manuale" card so the host can still proceed.
  const [ratePlans, setRatePlans] = useState([]);
  // iter171: picker "Cerca da rubrica" per riutilizzare un cliente già noto
  // (per esempio quando l'ospite chiama al telefono e ci dice solo il
  // cognome — non serve chiedere l'email per riconoscerlo).
  const [rubricaOpen, setRubricaOpen] = useState(false);
  const [rubricaQ, setRubricaQ] = useState("");
  const [rubricaCustomers, setRubricaCustomers] = useState([]);
  // iter171: banner "Cliente abituale" con sconto fedeltà 5% (già presente
  // sul checkout pubblico via /api/loyalty/check). Mostra CTA "Applica -5%".
  const [loyalty, setLoyalty] = useState(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/customers");
        if (!cancelled) setRubricaCustomers(Array.isArray(r.data?.customers) ? r.data.customers : []);
      } catch { if (!cancelled) setRubricaCustomers([]); }
    })();
    return () => { cancelled = true; };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/rate-plans");
        if (!cancelled) {
          const plans = Array.isArray(r.data?.rate_plans) ? r.data.rate_plans : [];
          setRatePlans(plans.filter((p) => p.active !== false));
        }
      } catch { if (!cancelled) setRatePlans([]); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) { setSmartSuggest(null); return; }
    // Reset the optional receipt attachment every time the dialog opens so
    // we don't carry over a file from a previous booking session.
    setReceiptB64("");
    setReceiptName("");
    if (isEdit) {
      // Edit mode: prefill from existing booking
      const b = editingBooking;
      setForm({
        first_name: b.guest?.first_name || "",
        last_name: b.guest?.last_name || "",
        email: b.guest?.email || "",
        phone: b.guest?.phone || "",
        room_id: b.room_id || rooms[0]?.id || "",
        guide_room_number: b.guide_room_number != null ? String(b.guide_room_number) : "",
        check_in: b.check_in || "",
        nights: b.nights || 1,
        guests: b.guests || 1,
        status: b.status || "confirmed",
        source: b.source || "manual",
        source_note: b.source_note || "",
        notes: b.notes || "",
        rate_type: b.rate_type || "manual",
        rate_plan_id: "",
        rate_plan_name: b.rate_plan_name || "",
        rate_discount_pct: Number(b.rate_discount_pct || 0),
        custom_nightly_rate: "",
        custom_total: b.total != null ? String(b.total) : "",
        discount_pct: "",
        booking_paid: !!b.booking_paid,
        payment_method: b.payment_method || "",
        deposit_amount: b.deposit_amount != null ? String(b.deposit_amount) : "",
        deposit_method: b.deposit_method || "",
        tassa_paid: !!b.tassa_paid,
        tassa_method: b.tassa_method || "",
        tassa_amount: b.tassa_amount != null ? String(b.tassa_amount) : "",
        tassa_note: b.tassa_note || "",
      });
      setExtraRooms([]);
    } else {
      setForm({
        ...EMPTY_FORM,
        check_in: (prefill?.check_in) || date,
        room_id: (prefill?.room_id) || rooms[0]?.id || "",
        // iter186 — se `prefill.check_in` e `prefill.check_out` esistono,
        // calcola nights così l'host non deve ri-selezionare le date.
        nights: (prefill?.check_in && prefill?.check_out)
          ? Math.max(1, Math.round((new Date(prefill.check_out) - new Date(prefill.check_in)) / 86400000))
          : (EMPTY_FORM.nights || 1),
        // Aggiungiamo una nota che segnala la provenienza iCal → contesto
        // utile in caso l'host abbia dubbi durante la compilazione.
        notes: prefill?.ical_uid
          ? `Da iCal Booking.com (uid=${(prefill.ical_uid || "").slice(0, 24)}…). Verifica il codice reale sull'extranet.`
          : "",
        // iter199 — Il backend accetta solo `walk_in|phone|manual`; per le
        // prenotazioni create da iCal usiamo `manual` (la provenienza
        // resta nelle note sopra). Prima passavamo `manual_from_ical` →
        // FastAPI rifiutava con 422 "Input should be 'walk_in', 'phone'
        // or 'manual'". Manteniamo comunque il supporto a `source_hint`
        // per eventuali estensioni future, ma normalizziamo qui.
        source: (["walk_in", "phone", "manual"].includes(prefill?.source_hint))
          ? prefill.source_hint
          : "manual",
      });
      setExtraRooms([]);
    }
  }, [open, date, rooms, isEdit, editingBooking, prefill]);

  // SMART SUGGEST: when email is typed, lookup customer prefs + previous bookings
  useEffect(() => {
    const email = (form.email || "").trim();
    if (!open || email.length < 5 || !email.includes("@")) { setSmartSuggest(null); return; }
    const timer = setTimeout(async () => {
      try {
        const [prefsR, custR] = await Promise.all([
          api.get(`/admin/customer-preferences/${encodeURIComponent(email)}`),
          api.get(`/admin/customers/${encodeURIComponent(email)}`).catch(() => null),
        ]);
        const prefs = prefsR.data;
        const cust = custR?.data;
        if ((cust && cust.profile) || prefs.custom_discount_pct || prefs.custom_nightly_rate) {
          setSmartSuggest({ prefs, profile: cust?.profile, bookings: cust?.bookings || [] });
        } else {
          setSmartSuggest(null);
        }
      } catch {
        setSmartSuggest(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.email, open]);

  const applySuggest = () => {
    if (!smartSuggest) return;
    const p = smartSuggest.profile || {};
    const pr = smartSuggest.prefs || {};
    setForm((f) => ({
      ...f,
      first_name: f.first_name || p.first_name || "",
      last_name: f.last_name || p.last_name || "",
      phone: f.phone || p.phone || "",
      custom_nightly_rate: pr.custom_nightly_rate || f.custom_nightly_rate || "",
      discount_pct: (pr.custom_discount_pct ?? "") || f.discount_pct || "",
      notes: f.notes || (pr.private_notes ? `[Pref: ${pr.private_notes}]` : ""),
    }));
    toast.success("Preferenze cliente applicate");
  };

  // iter171: pick a customer from the rubric — auto-fills guest fields
  // in one tap. Also closes the rubric drawer.
  const pickFromRubrica = (c) => {
    setForm((f) => ({
      ...f,
      first_name: c.first_name || f.first_name,
      last_name: c.last_name || f.last_name,
      email: c.email || f.email,
      phone: c.phone || f.phone,
    }));
    setRubricaOpen(false);
    setRubricaQ("");
    toast.success(`Cliente ${c.first_name || ""} ${c.last_name || ""} caricato dalla rubrica`.trim());
  };

  // iter171: check loyalty status when the guest email is set. Debounced
  // (500ms) to avoid spamming the API on every keystroke.
  useEffect(() => {
    const email = (form.email || "").trim();
    if (!open || !email || !email.includes("@") || email.length < 5) { setLoyalty(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/loyalty/check?email=${encodeURIComponent(email)}`);
        if (r.data?.is_returning) setLoyalty(r.data);
        else setLoyalty(null);
      } catch { setLoyalty(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.email, open]);

  // iter171: apply the loyalty 5% discount to the discount_pct field.
  const applyLoyalty = () => {
    if (!loyalty) return;
    const pct = Number(loyalty.discount_pct || 5);
    setForm((f) => ({ ...f, discount_pct: String(pct) }));
    toast.success(`Sconto fedeltà -${pct}% applicato`);
  };

  // Filtered rubric list (case-insensitive contains).
  const filteredRubrica = useMemo(() => {
    const q = rubricaQ.trim().toLowerCase();
    const list = rubricaCustomers;
    if (!q) return list.slice(0, 20);
    return list.filter((c) =>
      (c.last_name || "").toLowerCase().includes(q) ||
      (c.first_name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q),
    ).slice(0, 20);
  }, [rubricaCustomers, rubricaQ]);

  // Upload an optional payment receipt right after the booking is saved.
  // Wrapped in try/catch so a failed upload never rolls back the booking
  // — the host can still attach it from the booking detail later.
  const uploadReceiptIfAny = async (code, amount, method) => {
    if (!receiptB64 || !code) return;
    try {
      await api.post(`/bookings/${encodeURIComponent(code)}/upload-receipt`, {
        booking_code: code,
        method_id: method || null,
        amount: amount > 0 ? amount : null,
        purpose: "deposit",
        purpose_detail: "Caricato dall'host in fase di creazione",
        receipt_image_b64: receiptB64,
        guest_name: null,
        note: "manual_entry",
      });
      toast.success("📎 Ricevuta allegata alla prenotazione");
    } catch (e) {
      console.warn("[receipt upload]", e);
      toast.error("Prenotazione creata, ma il caricamento della ricevuta è fallito. Puoi riprovare dal dettaglio.");
    }
  };

  const submit = async () => {
    if (!form.first_name?.trim()) { toast.error("Nome obbligatorio"); return; }
    if (!form.room_id) { toast.error("Seleziona una camera"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        // EDIT MODE: PUT /admin/bookings/{id}
        const payload = {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          room_id: form.room_id,
          check_in: form.check_in,
          nights: Number(form.nights),
          guests: Number(form.guests),
          status: form.status,
          source_note: form.source_note,
          notes: form.notes,
          booking_paid: !!form.booking_paid,
          payment_method: form.payment_method || null,
          tassa_paid: !!form.tassa_paid,
          tassa_method: form.tassa_method || null,
          tassa_amount: form.tassa_amount === "" || form.tassa_amount == null ? null : Number(form.tassa_amount),
          tassa_note: form.tassa_note || null,
          rate_type: form.rate_type || "manual",
          rate_plan_name: form.rate_plan_name || "",
          rate_discount_pct: Number(form.rate_discount_pct || 0),
        };
        if (form.custom_total) payload.custom_total = Number(form.custom_total);
        else if (form.custom_nightly_rate) payload.custom_nightly_rate = Number(form.custom_nightly_rate);
        else if (form.discount_pct) payload.discount_pct = Number(form.discount_pct);
        if (form.guide_room_number !== "" && form.guide_room_number != null) {
          payload.guide_room_number = parseInt(form.guide_room_number, 10);
        } else {
          // Empty string explicitly clears assignment
          payload.guide_room_number = null;
        }

        const r = await api.put(`/admin/bookings/${editingBooking.id}`, payload);
        const cascade = r.data?.cascade || {};
        const cascadeMsg = Object.entries(cascade)
          .filter(([, v]) => v > 0)
          .map(([k, v]) => `${k}=${v}`)
          .join(" · ");
        toast.success(
          `✓ ${editingBooking.code} aggiornata${cascadeMsg ? ` · sync: ${cascadeMsg}` : ""}`,
          { duration: 5000 },
        );
        // Iter162: room-availability warnings are surfaced as a separate,
        // longer-lived toast so the host sees them BUT is not blocked from
        // saving. Policy per user request 07/2026: admin room field is a
        // category, not a physical unit — host decides overlaps.
        if (r.data?.warning) {
          toast.warning(r.data.warning, { duration: 10000 });
        }
        onCreated();
        return;
      }
      if (extraRooms.length === 0) {
        // Single-room: usa l'endpoint esistente (no break-change)
        const payload = {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          room_id: form.room_id,
          check_in: form.check_in,
          nights: Number(form.nights),
          guests: Number(form.guests),
          status: form.status,
          source: form.source,
          source_note: form.source_note,
          notes: form.notes,
          rate_type: form.rate_type || "manual",
          rate_plan_name: form.rate_plan_name || "",
          rate_discount_pct: Number(form.rate_discount_pct || 0),
        };
        if (form.custom_total) payload.custom_total = Number(form.custom_total);
        else if (form.custom_nightly_rate) payload.custom_nightly_rate = Number(form.custom_nightly_rate);
        else if (form.discount_pct) payload.discount_pct = Number(form.discount_pct);
        if (form.deposit_amount && Number(form.deposit_amount) > 0) {
          payload.deposit_amount = Number(form.deposit_amount);
          if (form.deposit_method) payload.deposit_method = form.deposit_method;
        }
        if (form.guide_room_number !== "" && form.guide_room_number != null) {
          payload.guide_room_number = parseInt(form.guide_room_number, 10);
        }
        // "Ospite effettivo" override — host prenota per qualcun altro.
        // Inviato solo quando la toggle è attiva E almeno un campo è
        // compilato, così il backend non crea record `guest_for` vuoti.
        if (form.booking_for_other) {
          const gfFirst = (form.guest_for_first_name || "").trim();
          const gfLast = (form.guest_for_last_name || "").trim();
          const gfEmail = (form.guest_for_email || "").trim();
          const gfPhone = (form.guest_for_phone || "").trim();
          if (gfFirst || gfLast || gfEmail || gfPhone) {
            payload.guest_for_first_name = gfFirst;
            payload.guest_for_last_name = gfLast;
            payload.guest_for_email = gfEmail;
            payload.guest_for_phone = gfPhone;
          }
        }

        const r = await api.post("/admin/bookings/manual", payload);
        if (r.data?.merged) {
          toast.success(`✓ Trovato duplicato: i dati del walk-in sono stati uniti alla prenotazione esistente ${r.data.code}`);
        } else {
          toast.success(`Prenotazione ${r.data.code} creata · €${r.data.total}`);
        }
        // Overlap warning: shown as a non-blocking longer toast so the host
        // knows the same category is over-assigned but the save proceeded.
        if (r.data?.warning) {
          toast.warning(r.data.warning, { duration: 10000 });
        }
        // One-tap WhatsApp confirmation deep link (only offered when the
        // guest has a phone number — otherwise the wa.me still opens but
        // the host has to pick a contact manually).
        if (r.data?.whatsapp_url && (form.guest?.phone || "").trim()) {
          toast.success(
            <span>
              Prenotazione creata — <strong>invia la conferma</strong>:
              <a
                href={r.data.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 underline text-emerald-600 font-semibold"
                data-testid="wa-manual-create-link"
              >
                💬 WhatsApp
              </a>
            </span>,
            { duration: 15000 },
          );
        }
        // Receipt is attached AFTER success so a failed upload doesn't
        // roll back the booking — we simply notify the host.
        await uploadReceiptIfAny(r.data?.code, Number(form.deposit_amount || 0), form.deposit_method);
      } else {
        // Multi-room: endpoint manual-multi
        const allRooms = [
          {
            room_id: form.room_id,
            nights: Number(form.nights),
            guests: Number(form.guests),
            rate_type: form.rate_type || "manual",
            rate_plan_name: form.rate_plan_name || "",
            rate_discount_pct: Number(form.rate_discount_pct || 0),
            ...(form.custom_total ? { custom_total: Number(form.custom_total) } :
                form.custom_nightly_rate ? { custom_nightly_rate: Number(form.custom_nightly_rate) } :
                form.discount_pct ? { discount_pct: Number(form.discount_pct) } : {}),
          },
          ...extraRooms.map((er) => ({
            room_id: er.room_id,
            nights: Number(er.nights || form.nights),
            guests: Number(er.guests || 2),
            guest_first_name: er.guest_first_name || "",
            guest_last_name: er.guest_last_name || "",
            guest_email: (er.guest_email || "").trim(),
            guest_phone: (er.guest_phone || "").trim(),
            // Tariffa per camera: usa quella esplicita della riga oppure
            // ricade su quella globale del gruppo (form.rate_type).
            rate_type: er.rate_type || form.rate_type || "manual",
            ...(er.guide_room_number !== "" && er.guide_room_number != null
              ? { guide_room_number: parseInt(er.guide_room_number, 10) }
              : {}),
            ...(er.custom_total ? { custom_total: Number(er.custom_total) } :
                er.custom_nightly_rate ? { custom_nightly_rate: Number(er.custom_nightly_rate) } :
                er.discount_pct ? { discount_pct: Number(er.discount_pct) } : {}),
          })),
        ];
        const missing = allRooms.find((r) => !r.room_id);
        if (missing) { toast.error("Seleziona una camera per ogni riga"); setBusy(false); return; }
        // Same room_id repeated is now ALLOWED — backend assigns each
        // duplicate to a different physical unit (unit_index 1..N).
        const payload = {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          check_in: form.check_in,
          rooms: allRooms,
          status: form.status,
          source: form.source,
          source_note: form.source_note,
          notes: form.notes,
        };
        if (form.deposit_amount && Number(form.deposit_amount) > 0) {
          payload.deposit_amount = Number(form.deposit_amount);
          if (form.deposit_method) payload.deposit_method = form.deposit_method;
        }
        const r = await api.post("/admin/bookings/manual-multi", payload);
        toast.success(`Gruppo ${r.data.group_code} · ${r.data.group_size} camere · €${r.data.total}`);
        // For group bookings the receipt is attached to the MASTER row
        // (group_index=1) — that's where the deposit lives in our schema.
        const masterCode = r.data?.bookings?.[0]?.code;
        await uploadReceiptIfAny(masterCode, Number(form.deposit_amount || 0), form.deposit_method);
        // Per-room holders that left only a phone (no email) need a
        // manual WhatsApp forward. We open each link in a new tab so the
        // host can shoot the message in one swipe instead of looking up
        // contacts later. Email invites have already been queued by the
        // backend automatically.
        const waInvites = r.data?.whatsapp_invites || [];
        if (waInvites.length > 0) {
          waInvites.forEach((inv, i) => {
            // Stagger the popups slightly so the browser doesn't drop them
            setTimeout(() => {
              try { window.open(inv.wa_url, "_blank", "noopener"); }
              catch (_) { /* popup blocked — show toast fallback */ }
            }, i * 250);
          });
          toast.info(
            `📲 Apro WhatsApp per ${waInvites.length} ospite${waInvites.length > 1 ? "i" : ""} (solo telefono indicato): ${waInvites.map((i) => i.holder_name).join(", ")}`,
            { duration: 8000 },
          );
        }
      }
      onCreated();
    } catch (e) {
      toast.error(errMsg(e, "Errore creazione"));
    } finally { setBusy(false); }
  };

  // estimated total preview
  const selectedRoom = rooms.find((r) => r.id === form.room_id);
  const baseRate = Number(selectedRoom?.price_per_night || 0);
  // Per-room subtotal helper — exposed so we can render the live cost
  // next to each "Camera aggiuntiva" card in the modal. The host can
  // immediately see "Camera #2 → €160" while she enters the rate, which
  // was the #1 friction point reported on the multi-room form.
  const calcRoomTotal = (rId, n, custTotal, custRate, discPct) => {
    if (custTotal) return Number(custTotal);
    if (custRate) return Number(custRate) * Number(n || 0);
    const room = rooms.find((r) => r.id === rId);
    const base = Number(room?.price_per_night || 0);
    let t = base * Number(n || 0);
    if (discPct) t = t * (1 - Number(discPct) / 100);
    return Math.round(t * 100) / 100;
  };

  const previewTotal = useMemo(() => {
    let total = calcRoomTotal(form.room_id, form.nights, form.custom_total, form.custom_nightly_rate, form.discount_pct);
    for (const er of extraRooms) {
      total += calcRoomTotal(er.room_id, er.nights || form.nights, er.custom_total, er.custom_nightly_rate, er.discount_pct);
    }
    return Math.round(total * 100) / 100;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, baseRate, extraRooms, rooms]);

  // Live "Saldo da pagare" = Totale stimato − Acconto incassato.
  // For a multi-room group MASTER booking the deposit covers the whole
  // group, so the saldo must be computed against the GROUP TOTAL (e.g.
  // €480), not just this single room's own price (€160) — otherwise the
  // host sees a misleading "saldo €15" when she actually still owes €335.
  //
  // iter182 — Live group total: quando si apre una prenotazione di gruppo
  // in edit, carichiamo i totali live dei siblings dal DB per mostrare la
  // somma corretta anche se una camera del gruppo è stata modificata da
  // un altro edit precedente (prima usavamo `editingBooking.group_total`
  // statico → vecchio valore quando cambiavi notti / prezzo di un figlio).
  const [groupSiblingsTotals, setGroupSiblingsTotals] = useState(null);
  useEffect(() => {
    if (!isEdit) { setGroupSiblingsTotals(null); return; }
    const gcode = editingBooking?.group_code;
    if (!gcode) { setGroupSiblingsTotals(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/bookings/group/${encodeURIComponent(gcode)}`);
        if (cancelled) return;
        const arr = r.data?.items || r.data?.bookings || [];
        setGroupSiblingsTotals(arr);
      } catch {
        setGroupSiblingsTotals(null); // fallback: usiamo il valore statico
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editingBooking?.group_code, editingBooking?.updated_at]);

  const liveGroupTotal = useMemo(() => {
    if (!groupSiblingsTotals || groupSiblingsTotals.length === 0) return null;
    // Per la camera correntemente in edit usa il previewTotal (che si
    // aggiorna live mentre l'host cambia notti/prezzo), per le altre
    // usa il total salvato in DB.
    return groupSiblingsTotals.reduce((sum, b) => {
      if (b.id === editingBooking?.id) return sum + Number(previewTotal || 0);
      return sum + Number(b.total || 0);
    }, 0);
  }, [groupSiblingsTotals, previewTotal, editingBooking?.id]);

  const balanceDue = useMemo(() => {
    const dep = Number(form.deposit_amount || 0);
    const staticGroupTotal = Number(editingBooking?.group_total || 0);
    const groupTotal = liveGroupTotal != null ? liveGroupTotal : staticGroupTotal;
    const isGroupMaster =
      isEdit
      && (editingBooking?.group_size || 1) > 1
      && (editingBooking?.group_index || 1) === 1
      && groupTotal > 0;
    const base = isGroupMaster ? groupTotal : previewTotal;
    const bal = base - (dep > 0 ? dep : 0);
    return Math.max(0, Math.round(bal * 100) / 100);
  }, [previewTotal, form.deposit_amount, isEdit, editingBooking, liveGroupTotal]);

  const addExtraRoom = () => {
    // Default to the same primary room (most common case for group bookings:
    // 3 matrimoniali). Host can switch to a different room from the dropdown.
    setExtraRooms((er) => [...er, {
      room_id: form.room_id || rooms[0]?.id || "",
      nights: form.nights,
      guests: 2,
      guide_room_number: "",
      guest_first_name: "",
      guest_last_name: "",
      guest_email: "",
      guest_phone: "",
      custom_nightly_rate: "",
      custom_total: "",
      discount_pct: "",
    }]);
  };
  const updateExtra = (idx, patch) => setExtraRooms((er) => er.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeExtra = (idx) => setExtraRooms((er) => er.filter((_, i) => i !== idx));

  // Chiama l'endpoint di aggiunta camera al gruppo di un master già esistente.
  // Usato solo in edit mode.
  const submitAddSibling = async () => {
    if (!editingBooking?.code) return;
    if (!addSiblingForm.room_id) {
      toast.error("Seleziona una camera per la nuova prenotazione.");
      return;
    }
    if (!addSiblingForm.guest_first_name.trim() || !addSiblingForm.guest_last_name.trim()) {
      toast.error("Inserisci nome e cognome del nuovo ospite.");
      return;
    }
    setAddingSibling(true);
    try {
      const body = {
        room_id: addSiblingForm.room_id,
        guest_first_name: addSiblingForm.guest_first_name.trim(),
        guest_last_name: addSiblingForm.guest_last_name.trim(),
      };
      const em = addSiblingForm.guest_email.trim();
      if (em) body.guest_email = em;
      const gn = Number(addSiblingForm.guests);
      if (Number.isFinite(gn) && gn > 0) body.guests = gn;
      const grn = (addSiblingForm.guide_room_number || "").trim();
      if (grn) body.guide_room_number = grn;
      const ct = Number(addSiblingForm.custom_total);
      if (Number.isFinite(ct) && ct > 0) body.custom_total = ct;
      const res = await api.post(
        `/admin/bookings/${encodeURIComponent(editingBooking.code)}/ensure-sibling`,
        body,
      );
      const d = res.data || {};
      if (d.already_has_sibling) {
        // iter185: ora questo scatta solo se la camera scelta è già nel gruppo
        // (duplicate_room_id), non più per limite hardcoded a 2 camere.
        toast.info("Questa camera è già presente nel gruppo. Scegline un'altra.");
      } else if (d.created_sibling?.code) {
        toast.success(`Camera aggiunta: ${d.created_sibling.code} · ${d.created_sibling.guest} · Totale gruppo: € ${(d.new_group_total || 0).toFixed(2)}`);
      } else {
        toast.success("Camera aggiunta al gruppo.");
      }
      setAddSiblingOpen(false);
      setAddSiblingForm({ room_id: "", guest_first_name: "", guest_last_name: "", guest_email: "", guests: "", guide_room_number: "", custom_total: "" });
      // Chiudi il dialog di modifica e ricarica l'elenco parent
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e) || "Errore durante l'aggiunta della camera.");
    } finally {
      setAddingSibling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-booking-dialog" : "new-booking-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <FileEdit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            <span className="flex-1">
              {isEdit ? `Modifica prenotazione ${editingBooking?.code || ""}` : "Nuova prenotazione"}
            </span>
            {/* Discreet "+" — aggiunge una camera al gruppo. Visibile solo in
                edit mode, subito accanto al titolo. Click → apre il mini-form
                sotto. */}
            {isEdit && !addSiblingOpen && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setAddSiblingForm((f) => ({ ...f, room_id: form.room_id || rooms[0]?.id || "" }));
                    setAddSiblingOpen(true);
                  }}
                  title="Aggiungi camera al gruppo"
                  aria-label="Aggiungi camera al gruppo"
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[#AC5D45] hover:bg-[#F7DFC4] hover:text-[#8E4A36] transition-colors border border-[#AC5D45]/40"
                  data-testid="eb-add-sibling-open"
                >
                  <Plus className="w-4 h-4" />
                </button>
                {/* iter187 — Duplica ultima camera: precompila il form con
                    ospiti/prezzo/n°camera+1 dell'ultimo sibling. Utile per
                    gruppi grandi (gita, famiglia numerosa). */}
                {(groupSiblingsTotals?.length || 0) >= 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const last = [...(groupSiblingsTotals || [])].sort(
                        (a, b) => (Number(b.group_index) || 0) - (Number(a.group_index) || 0),
                      )[0];
                      if (!last) return;
                      const grnNext = Number(last.guide_room_number);
                      setAddSiblingForm({
                        room_id: last.room_id || form.room_id || rooms[0]?.id || "",
                        guest_first_name: "",
                        guest_last_name: "",
                        guest_email: "",
                        guests: last.guests ? String(last.guests) : "",
                        guide_room_number: Number.isFinite(grnNext) ? String(grnNext + 1) : "",
                        custom_total: last.total ? String(last.total) : "",
                      });
                      setAddSiblingOpen(true);
                    }}
                    title="Duplica l'ultima camera del gruppo (stessi ospiti/prezzo, n° camera +1)"
                    aria-label="Duplica ultima camera"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[#1E3A5F] hover:bg-[#DDE7F0] hover:text-[#0F2842] transition-colors border border-[#1E3A5F]/30"
                    data-testid="eb-duplicate-sibling-open"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Mini-form "Aggiungi camera al gruppo" — visibile solo dopo il
            click sul + accanto al titolo. Stessa fisionomia di un blocco
            di form standard, niente sfondo colorato appariscente. */}
        {isEdit && addSiblingOpen && (
          <div className="bg-white border border-[#AC5D45]/40 rounded p-2.5 space-y-2 mb-1" data-testid="eb-add-sibling">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-[#AC5D45]">+ Nuova camera nel gruppo</span>
              <button
                type="button"
                onClick={() => {
                  setAddSiblingOpen(false);
                  setAddSiblingForm({ room_id: "", guest_first_name: "", guest_last_name: "", guest_email: "", guests: "", guide_room_number: "", custom_total: "" });
                }}
                className="text-[10px] text-[#635F59] hover:underline"
                data-testid="eb-sibling-cancel"
              >
                Annulla
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-[10px]">Camera *</Label>
                <Select
                  value={addSiblingForm.room_id}
                  onValueChange={(v) => {
                    const r2 = rooms.find((x) => x.id === v);
                    const cap = Number(r2?.capacity || 0);
                    setAddSiblingForm((f) => ({
                      ...f,
                      room_id: v,
                      guests: cap ? String(cap) : f.guests,
                    }));
                  }}
                >
                  <SelectTrigger className="h-8" data-testid="eb-sibling-room">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {rooms.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{_str(r.name)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Ospiti</Label>
                <Input
                  className="h-8"
                  type="number"
                  min={1}
                  value={addSiblingForm.guests}
                  onChange={(e) => setAddSiblingForm((f) => ({ ...f, guests: e.target.value }))}
                  placeholder="2"
                  data-testid="eb-sibling-guests"
                />
              </div>
            </div>
            {/* iter185/186: numero camera fisica + prezzo camera (tariffa manuale)
                modificabili subito senza dover salvare e riaprire. */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">N° camera fisica (opzionale)</Label>
                <Input
                  className="h-8"
                  value={addSiblingForm.guide_room_number}
                  onChange={(e) => setAddSiblingForm((f) => ({ ...f, guide_room_number: e.target.value }))}
                  placeholder="es. 13"
                  data-testid="eb-sibling-guide-room-number"
                />
              </div>
              <div>
                <Label className="text-[10px]">Prezzo camera € (opzionale)</Label>
                <Input
                  className="h-8"
                  type="number"
                  min={0}
                  step="0.01"
                  value={addSiblingForm.custom_total}
                  onChange={(e) => setAddSiblingForm((f) => ({ ...f, custom_total: e.target.value }))}
                  placeholder="Lascia vuoto per tariffa standard"
                  data-testid="eb-sibling-custom-total"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Nome ospite *</Label>
                <Input
                  className="h-8"
                  value={addSiblingForm.guest_first_name}
                  onChange={(e) => setAddSiblingForm((f) => ({ ...f, guest_first_name: e.target.value }))}
                  data-testid="eb-sibling-first"
                />
              </div>
              <div>
                <Label className="text-[10px]">Cognome ospite *</Label>
                <Input
                  className="h-8"
                  value={addSiblingForm.guest_last_name}
                  onChange={(e) => setAddSiblingForm((f) => ({ ...f, guest_last_name: e.target.value }))}
                  data-testid="eb-sibling-last"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px]">Email (opzionale, default: come master)</Label>
              <Input
                className="h-8"
                type="email"
                value={addSiblingForm.guest_email}
                onChange={(e) => setAddSiblingForm((f) => ({ ...f, guest_email: e.target.value }))}
                placeholder={form.email || "cliente@email.it"}
                data-testid="eb-sibling-email"
              />
            </div>
            <div className="flex items-center justify-end pt-0.5">
              <Button
                size="sm"
                className="bg-[#AC5D45] hover:bg-[#8E4A36] text-white h-8 text-xs"
                onClick={submitAddSibling}
                disabled={addingSibling}
                data-testid="eb-sibling-save"
              >
                {addingSibling ? "Aggiungo…" : "Salva camera"}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {/* iter171: Rubrica clienti — pick a returning customer by name */}
          {!isEdit && (
            <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3" data-testid="nb-rubrica-panel">
              <button
                type="button"
                onClick={() => setRubricaOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-2 text-left"
                data-testid="nb-rubrica-toggle"
              >
                <div className="flex items-center gap-2">
                  <UsersIcon className="w-4 h-4 text-[#AC5D45]" />
                  <span className="text-sm font-semibold text-[#1E3A5F]">Cliente già stato da noi?</span>
                  <span className="text-[11px] text-[#635F59]">— cerca in rubrica per cognome / nome / email</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#635F59] transition-transform ${rubricaOpen ? "rotate-180" : ""}`} />
              </button>
              {rubricaOpen && (
                <div className="mt-2 space-y-2">
                  <Input
                    value={rubricaQ}
                    onChange={(e) => setRubricaQ(e.target.value)}
                    placeholder="Cerca cognome, nome o email…"
                    className="h-8 text-xs"
                    data-testid="nb-rubrica-search"
                    autoFocus
                  />
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {filteredRubrica.length === 0 ? (
                      <p className="text-[11px] text-[#9b948a] italic px-1">Nessun cliente trovato.</p>
                    ) : filteredRubrica.map((c) => (
                      <button
                        type="button"
                        key={c.id || c.email || `${c.first_name}-${c.last_name}`}
                        onClick={() => pickFromRubrica(c)}
                        className="w-full text-left bg-white hover:bg-[#FFF8F0] active:bg-[#FFEBD8] border border-[#E8E2D9] hover:border-[#AC5D45] rounded px-2 py-2 text-xs flex items-center justify-between gap-2"
                        data-testid={`nb-rubrica-pick-${c.email || c.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-[#1E3A5F] truncate">{c.first_name} {c.last_name}</div>
                          <div className="text-[10px] text-[#635F59] truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                        </div>
                        <div className="text-[10px] text-[#635F59] shrink-0">{c.total_bookings || 0} prenot.</div>
                        <ChevronRight className="w-3.5 h-3.5 text-[#AC5D45] shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* iter171: Loyalty banner — shown when the current email belongs
              to a returning customer. One-tap "Applica -5%" to fill the
              discount_pct field. */}
          {loyalty && loyalty.is_returning && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="nb-loyalty-banner">
              <div className="flex items-center gap-2 min-w-0">
                <Star className="w-4 h-4 text-emerald-700 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-emerald-900">
                    Cliente abituale — {loyalty.completed_bookings || 1} soggiorn{(loyalty.completed_bookings || 1) === 1 ? "o" : "i"} precedent{(loyalty.completed_bookings || 1) === 1 ? "e" : "i"}
                  </div>
                  <div className="text-[11px] text-emerald-800">Puoi applicare lo sconto fedeltà del {Number(loyalty.discount_pct || 5)}%.</div>
                </div>
              </div>
              <Button
                size="sm"
                onClick={applyLoyalty}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                data-testid="nb-loyalty-apply"
              >
                Applica -{Number(loyalty.discount_pct || 5)}%
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="nb-first" />
            </div>
            <div>
              <Label className="text-xs">Cognome</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="nb-last" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="nb-email" placeholder="cliente@email.it" />
            </div>
            <div>
              <Label className="text-xs">Telefono</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="nb-phone" />
            </div>
          </div>

          {/* OSPITE EFFETTIVO — toggle (host prenota per qualcun altro) */}
          <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3" data-testid="nb-booking-for-other">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.booking_for_other}
                onChange={(e) => setForm({ ...form, booking_for_other: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#AC5D45]"
                data-testid="nb-booking-for-other-toggle"
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-[#1E3A5F]">L'ospite che soggiorna è diverso da chi prenota</span>
                <p className="text-[11px] text-[#635F59] mt-0.5 leading-snug">
                  Es. un genitore prenota per il figlio, un'amica regala un soggiorno. I dati qui sopra restano della persona che paga; qui sotto inserisci chi dormirà davvero in struttura.
                </p>
              </div>
            </label>
            {form.booking_for_other && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" data-testid="nb-guest-for-fields">
                <div>
                  <Label className="text-[11px] text-[#635F59]">Nome ospite effettivo *</Label>
                  <Input value={form.guest_for_first_name} onChange={(e) => setForm({ ...form, guest_for_first_name: e.target.value })} className="h-9" placeholder="Nome di chi dorme" data-testid="nb-gf-firstname" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#635F59]">Cognome ospite effettivo *</Label>
                  <Input value={form.guest_for_last_name} onChange={(e) => setForm({ ...form, guest_for_last_name: e.target.value })} className="h-9" placeholder="Cognome di chi dorme" data-testid="nb-gf-lastname" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#635F59]">Email (facoltativa)</Label>
                  <Input type="email" value={form.guest_for_email} onChange={(e) => setForm({ ...form, guest_for_email: e.target.value })} className="h-9" placeholder="ospite@email.it" data-testid="nb-gf-email" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#635F59]">Telefono (facoltativo)</Label>
                  <Input value={form.guest_for_phone} onChange={(e) => setForm({ ...form, guest_for_phone: e.target.value })} className="h-9" placeholder="+39 …" data-testid="nb-gf-phone" />
                </div>
              </div>
            )}
          </div>

          {/* SMART SUGGEST */}
          {smartSuggest && (
            <div className="bg-[#FDF1E6] border border-[#AC5D45]/40 rounded-lg p-3 flex items-start gap-2" data-testid="smart-suggest">
              <Sparkles className="w-4 h-4 text-[#AC5D45] mt-0.5" />
              <div className="flex-1 text-xs">
                <div className="font-semibold text-[#AC5D45] flex items-center gap-1.5">
                  💡 Cliente già conosciuto
                  {smartSuggest.bookings?.length >= 2 && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}
                </div>
                <div className="text-[#635F59] mt-0.5">
                  {smartSuggest.profile && (
                    <>
                      <b>{smartSuggest.profile.full_name}</b> · {smartSuggest.bookings.length} soggiorni · €{smartSuggest.profile.total_spent} spesi
                    </>
                  )}
                  {smartSuggest.prefs.custom_discount_pct && (
                    <span className="text-emerald-700 ml-1">· Sconto preferenziale <b>-{smartSuggest.prefs.custom_discount_pct}%</b></span>
                  )}
                  {smartSuggest.prefs.custom_nightly_rate && (
                    <span className="text-emerald-700 ml-1">· Tariffa speciale <b>€{smartSuggest.prefs.custom_nightly_rate}/notte</b></span>
                  )}
                  {smartSuggest.prefs.private_notes && (
                    <div className="mt-1 italic">"{smartSuggest.prefs.private_notes}"</div>
                  )}
                </div>
              </div>
              <Button size="sm" onClick={applySuggest} data-testid="smart-apply" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white shrink-0">
                Applica
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Camera *</Label>
              <Select value={form.room_id} onValueChange={(v) => {
                const r2 = rooms.find((x) => x.id === v);
                // Pre-fill guests with room capacity for the main room too
                // (same UX as the extra rooms). The host can still override.
                const cap2 = Number(r2?.capacity || 0);
                setForm((prev) => ({
                  ...prev,
                  room_id: v,
                  guests: cap2 || prev.guests || 2,
                }));
              }}>
                <SelectTrigger data-testid="nb-room"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {rooms.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{_str(r.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">N° camera fisica (opzionale)</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="es. 13"
                value={form.guide_room_number}
                onChange={(e) => setForm({ ...form, guide_room_number: e.target.value.replace(/[^0-9]/g, "") })}
                data-testid="nb-guide-room-number"
                title="Numero della camera fisica (es. 11, 13, 15) — sarà la stessa che vedi nelle pillole del calendario. Si sincronizza con l'App Guida."
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Stato</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="nb-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">In attesa</SelectItem>
                <SelectItem value="confirmed">Confermata</SelectItem>
                <SelectItem value="checked_in">Check-in fatto</SelectItem>
                <SelectItem value="completed">Completata</SelectItem>
                <SelectItem value="cancelled">Annullata</SelectItem>
                <SelectItem value="rejected">Rifiutata</SelectItem>
                <SelectItem value="frozen">Congelata</SelectItem>
                <SelectItem value="no_show">Mancata presentazione</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Data arrivo *</Label>
              <DateInputIT value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} data-testid="nb-checkin" />
            </div>
            <div>
              <Label className="text-xs">Notti *</Label>
              <Input type="number" min={1} value={form.nights} onChange={(e) => setForm({ ...form, nights: e.target.value })} data-testid="nb-nights" />
            </div>
            <div>
              <Label className="text-xs">N° ospiti</Label>
              <Input type="number" min={1} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="nb-guests" />
            </div>
          </div>

          {/* Tariffa applicata — selettore stile booking pubblica. Il
              backend ricalcola automaticamente il totale (sconto −10% per
              non_refundable) quando NON è impostato un "totale fisso"
              esplicito. */}
          <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3" data-testid="nb-rate-type">
            <Label className="text-xs font-semibold text-[#1E3A5F] flex items-center gap-1.5 mb-2">
              <Star className="w-3.5 h-3.5 text-[#E3A857]" /> Piano tariffario applicato
              {ratePlans.length > 0 && (
                <span className="text-[10px] text-[#635F59] font-normal">({ratePlans.length} piani configurati)</span>
              )}
            </Label>
            <div className={`grid gap-2 ${ratePlans.length + 1 > 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-3"}`}>
              {/* Dynamic plans loaded from /admin/rate-plans */}
              {ratePlans.map((p) => {
                const discRaw = Number(p.discount_pct || 0);
                const discAbs = Math.abs(discRaw);
                const cancelMap = {
                  flexible_7d: "deposit_30",
                  flexible_3d: "deposit_30",
                  flexible_21d: "deposit_30",
                  non_refundable: "non_refundable",
                  monthly: "non_refundable",
                };
                const ctype = cancelMap[p.cancellation_type] || "deposit_30";
                const picked = form.rate_plan_id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      rate_type: ctype,
                      rate_plan_id: p.id,
                      rate_plan_name: p.name,
                      rate_discount_pct: discRaw,
                    })}
                    data-testid={`nb-rate-plan-${p.id}`}
                    className={`text-left p-2.5 rounded-md border-2 transition ${picked ? "border-[#AC5D45] bg-white shadow-sm" : "border-[#E8E2D9] bg-white/60 hover:bg-white"}`}
                  >
                    <div className="text-xs font-semibold text-[#1E3A5F] flex items-center justify-between gap-1 flex-wrap">
                      <span className="truncate">{p.name}</span>
                      {discRaw < 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-[#E3A857] text-[#1E3A5F] font-bold">−{discAbs}%</span>
                      )}
                      {discRaw > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-rose-200 text-rose-800 font-bold">+{discRaw}%</span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#635F59] mt-0.5 leading-tight">
                      {({
                        flexible_7d: "Acconto 30% · saldo al check-in",
                        flexible_3d: "Acconto 30% · saldo al check-in",
                        flexible_21d: "Acconto 30% · prenot. anticipata",
                        non_refundable: "Pagamento subito · non rimborsabile",
                        monthly: "Soggiorno mensile · non rimborsabile",
                      })[p.cancellation_type] || p.cancellation_type || "—"}
                    </div>
                  </button>
                );
              })}
              {/* Manual fallback — always present so the host can override */}
              <button
                type="button"
                onClick={() => setForm({ ...form, rate_type: "manual", rate_plan_id: "", rate_plan_name: "", rate_discount_pct: 0 })}
                data-testid="nb-rate-manual"
                className={`text-left p-2.5 rounded-md border-2 transition ${form.rate_type === "manual" && !form.rate_plan_id ? "border-[#635F59] bg-white shadow-sm" : "border-[#E8E2D9] bg-white/60 hover:bg-white"}`}
              >
                <div className="text-xs font-semibold text-[#1E3A5F]">Manuale</div>
                <div className="text-[10px] text-[#635F59] mt-0.5 leading-tight">Inserisci tu i prezzi qui sotto</div>
              </button>
            </div>
            {form.rate_plan_id && form.rate_discount_pct !== 0 && !form.custom_total && (
              <p className="text-[10px] text-emerald-700 mt-2">
                ✓ Verrà applicato {form.rate_discount_pct < 0 ? `lo sconto −${Math.abs(form.rate_discount_pct)}%` : `la maggiorazione +${form.rate_discount_pct}%`} sul totale (piano <b>{form.rate_plan_name}</b>) — a meno che tu non imposti un "Totale fisso" qui sotto.
              </p>
            )}
          </div>

          {/* Prezzo */}
          <details className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3" data-testid="nb-pricing">
            <summary className="cursor-pointer text-sm font-semibold text-[#1E3A5F] flex items-center justify-between">
              <span>Prezzo personalizzato (opzionale)</span>
              <span className="font-mono text-[#AC5D45] text-sm" data-testid="nb-main-subtotal">
                € {calcRoomTotal(form.room_id, form.nights, form.custom_total, form.custom_nightly_rate, form.discount_pct).toFixed(2)}
              </span>
            </summary>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <Label className="text-xs">Tariffa/notte €</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.custom_nightly_rate}
                  onChange={(e) => {
                    // iter172: quando l'host modifica la tariffa/notte
                    // azzeriamo il "Totale fisso" (override manuale
                    // preesistente): altrimenti la priorità lo terrebbe
                    // invariato e la modifica sembrerebbe non funzionare.
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      custom_nightly_rate: v,
                      custom_total: v ? "" : f.custom_total,
                    }));
                  }}
                  placeholder={`base ${baseRate}`}
                  data-testid="nb-rate"
                />
              </div>
              <div>
                <Label className="text-xs">Sconto %</Label>
                <Input type="number" step="0.1" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} data-testid="nb-discount" />
              </div>
              <div>
                <Label className="text-xs">Totale fisso €</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.custom_total}
                  onChange={(e) => {
                    // Simmetrico: se l'host imposta il totale fisso
                    // manualmente, la tariffa/notte non deve rimanere
                    // a coprire il valore.
                    const v = e.target.value;
                    setForm((f) => ({
                      ...f,
                      custom_total: v,
                      custom_nightly_rate: v ? "" : f.custom_nightly_rate,
                    }));
                  }}
                  placeholder="override"
                  data-testid="nb-total"
                />
              </div>
            </div>
            <p className="text-[10px] text-[#635F59] mt-2">
              I due campi sono <b>mutualmente esclusivi</b>: modificando uno
              l'altro viene azzerato. Priorità applicata: <b>Totale fisso</b> &gt;
              <b> Tariffa/notte × notti</b> &gt; <b>Tariffa base − sconto</b>.
            </p>
          </details>

          {/* === MULTI-ROOM (only in CREATE mode — editing only affects single bookings) === */}
          {!isEdit && (
          <div className="bg-[#FDF1E6]/40 border border-[#AC5D45]/30 rounded-lg p-3 space-y-3" data-testid="nb-multi-room">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#1E3A5F] flex items-center gap-1.5">
                <BedDouble className="w-4 h-4 text-[#AC5D45]" /> Camere aggiuntive
                {extraRooms.length > 0 && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 bg-[#AC5D45] text-white text-[10px] rounded">
                    +{extraRooms.length}
                  </span>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={addExtraRoom} data-testid="nb-add-room">
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi camera
              </Button>
            </div>
            {extraRooms.length === 0 ? (
              <p className="text-[11px] text-[#635F59] italic">
                Una sola camera. Premi <b>+ Aggiungi camera</b> per creare una prenotazione di gruppo (più camere, stesse date, un'unica intestatario o intestatari diversi per stanza).
              </p>
            ) : (
              extraRooms.map((er, idx) => (
                <div key={er._uid || `er-${idx}`} className="bg-white border border-[#E8E2D9] rounded p-2.5 space-y-2" data-testid={`nb-extra-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#AC5D45]">Camera #{idx + 2}</span>
                    <button onClick={() => removeExtra(idx)} className="text-[10px] text-rose-600 hover:underline" data-testid={`nb-extra-remove-${idx}`}>
                      Rimuovi
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <Label className="text-[10px]">Camera</Label>
                      <Select value={er.room_id} onValueChange={(v) => {
                        const r2 = rooms.find((x) => x.id === v);
                        // Pre-fill guests with the room's capacity so the
                        // host doesn't have to type it every time. They can
                        // still override the value freely afterwards.
                        const cap2 = Number(r2?.capacity || 0);
                        updateExtra(idx, {
                          room_id: v,
                          guests: cap2 || er.guests || 2,
                        });
                      }}>
                        <SelectTrigger className="h-8" data-testid={`nb-extra-room-${idx}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          {rooms.map((r) => (
                            <SelectItem key={r.id} value={r.id}>{_str(r.name)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px]">Notti</Label>
                      <Input className="h-8" type="number" min={1} value={er.nights} onChange={(e) => updateExtra(idx, { nights: e.target.value })} data-testid={`nb-extra-nights-${idx}`} />
                    </div>
                  </div>
                  {(() => {
                    const r2 = rooms.find((x) => x.id === er.room_id);
                    const grn2 = (r2?.guide_room_numbers || []).join(", ");
                    return (
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <Label className="text-[10px]">Ospiti</Label>
                          <Input
                            className="h-8"
                            type="number"
                            min={1}
                            value={er.guests}
                            onChange={(e) => updateExtra(idx, { guests: e.target.value })}
                            data-testid={`nb-extra-guests-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">N° camera</Label>
                          <Input
                            className="h-8"
                            type="number"
                            min={1}
                            value={er.guide_room_number}
                            onChange={(e) => updateExtra(idx, { guide_room_number: e.target.value })}
                            placeholder={grn2 || "—"}
                            data-testid={`nb-extra-guide-num-${idx}`}
                            title={grn2 ? `Suggeriti: ${grn2}` : "Numero fisico della stanza (vedi App Guida)"}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Tariffa/notte</Label>
                          <Input className="h-8" type="number" step="0.01" value={er.custom_nightly_rate} onChange={(e) => updateExtra(idx, { custom_nightly_rate: e.target.value })} placeholder="auto" />
                        </div>
                        <div>
                          <Label className="text-[10px]">Sconto %</Label>
                          <Input className="h-8" type="number" step="0.1" value={er.discount_pct} onChange={(e) => updateExtra(idx, { discount_pct: e.target.value })} />
                        </div>
                      </div>
                    );
                  })()}
                  {/* Live subtotal so the host can immediately see what
                      this room costs (was the #1 friction in the multi-
                      room form: she'd enter rate × nights and wonder why
                      no per-room total appeared). */}
                  {(() => {
                    const sub = calcRoomTotal(
                      er.room_id,
                      er.nights || form.nights,
                      er.custom_total,
                      er.custom_nightly_rate,
                      er.discount_pct,
                    );
                    return (
                      <div className="flex items-center justify-between bg-[#FDFBF7] border-t border-[#E8E2D9] -mx-2.5 -mb-2.5 mt-1 px-2.5 py-1.5 rounded-b" data-testid={`nb-extra-subtotal-${idx}`}>
                        <span className="text-[10px] text-[#635F59] uppercase tracking-wider">Subtotale camera</span>
                        <span className="font-mono font-semibold text-[#1E3A5F] text-sm">€ {sub.toFixed(2)}</span>
                      </div>
                    );
                  })()}
                  <details className="text-[10px]">
                    <summary className="cursor-pointer text-[#635F59]">Intestatario diverso per questa camera (opzionale)</summary>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Input className="h-8" placeholder="Nome" value={er.guest_first_name} onChange={(e) => updateExtra(idx, { guest_first_name: e.target.value })} data-testid={`nb-extra-firstname-${idx}`} />
                      <Input className="h-8" placeholder="Cognome" value={er.guest_last_name} onChange={(e) => updateExtra(idx, { guest_last_name: e.target.value })} data-testid={`nb-extra-lastname-${idx}`} />
                      <Input className="h-8" type="email" placeholder="Email (per anagrafica)" value={er.guest_email} onChange={(e) => updateExtra(idx, { guest_email: e.target.value })} data-testid={`nb-extra-email-${idx}`} />
                      <Input className="h-8" type="tel" placeholder="Telefono" value={er.guest_phone} onChange={(e) => updateExtra(idx, { guest_phone: e.target.value })} data-testid={`nb-extra-phone-${idx}`} />
                    </div>
                    {er.guest_email && er.guest_email !== form.email && (
                      <p className="text-[10px] text-emerald-700 mt-1.5 leading-snug" data-testid={`nb-extra-invite-hint-${idx}`}>
                        ✉️ Riceverà una email con il proprio codice e il link per compilare la sua anagrafica.
                      </p>
                    )}
                  </details>
                </div>
              ))
            )}
          </div>
          )}


          <div>
            <Label className="text-xs">Provenienza</Label>
            <Input
              value={form.source_note}
              onChange={(e) => {
                const val = e.target.value;
                // If the host is now classifying where the booking came from,
                // the walk-in indicator is no longer meaningful — demote the
                // internal `source` from `walk_in` to `manual` so the amber
                // dot next to the guest name disappears. The user requested
                // this because the WALK-IN badge was showing even for phone
                // bookings where Provenienza had been set explicitly.
                setForm((f) => ({
                  ...f,
                  source_note: val,
                  source: val.trim() && f.source === "walk_in" ? "manual" : f.source,
                }));
              }}
              placeholder="es. WhatsApp · Telefono · Amico di un cliente · Booking.com · Walk-in"
              data-testid="nb-source-note"
            />
            <p className="text-[10px] text-[#635F59] mt-1">Scrivi liberamente da dove è arrivata la prenotazione (per le tue statistiche interne).</p>
          </div>

          <div>
            <Label className="text-xs">Note</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="nb-notes" />
          </div>

          {/* ACCONTO — sempre visibile (anche durante la creazione) così l'host
              non deve riaprire la prenotazione dopo per inserirlo. In group
              bookings questo valore va sulla prenotazione master (1/N). */}
          <div className="bg-[#FFF5EE] border border-[#AC5D45]/30 rounded-lg p-3" data-testid="nb-deposit-section">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h4 className="font-display text-sm text-[#1E3A5F]">💰 Acconto incassato</h4>
              <span className="text-[10px] text-[#635F59] italic">opzionale</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Importo (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.deposit_amount}
                  onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })}
                  className="h-8"
                  data-testid="nb-deposit-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Metodo</Label>
                <Select
                  value={form.deposit_method || "none"}
                  onValueChange={(v) => setForm({ ...form, deposit_method: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="h-8" data-testid="nb-deposit-method"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="sumup">SumUp</SelectItem>
                    <SelectItem value="satispay">Satispay</SelectItem>
                    <SelectItem value="postepay">PostePay</SelectItem>
                    <SelectItem value="carta">Carta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Iter167: Riepilogo economico completo — sempre visibile.
                Mostra Totale, Acconto atteso (in base a rate_type),
                Acconto incassato (se >0), Saldo da pagare, metodo. Il
                colore del box cambia in base allo stato pagamenti:
                verde = tutto pagato, ambra = parzialmente, blu neutro
                = nessun pagamento ancora. */}
            {(() => {
              // iter189 — Il "Totale prenotazione" del riepilogo economico deve
              // riflettere la SOMMA di camera base + tutte le extraRooms.
              // Prima usavamo `form.custom_total` che è solo l'override
              // manuale della camera principale → quando l'host aggiungeva
              // una seconda camera nel modal "Nuova Prenotazione", il totale
              // restava fermo sul prezzo della prima. `previewTotal` è già
              // calcolato correttamente sommando base + extraRooms.
              const total = Number(previewTotal || 0);
              const rt = form.rate_type || "manual";
              // Acconto ATTESO in base al rate plan (ciò che il cliente dovrebbe versare)
              const expectedDeposit = rt === "non_refundable" ? total :
                                       rt === "deposit_30" ? +(total * 0.30).toFixed(2) : 0;
              const paidDeposit = Number(form.deposit_amount || 0);
              const balance = Math.max(0, total - paidDeposit);
              const isFullyPaid = paidDeposit >= total && total > 0;
              const isPartial = paidDeposit > 0 && paidDeposit < total;
              const statusBg = isFullyPaid ? "bg-emerald-50 border-emerald-300"
                               : isPartial ? "bg-amber-50 border-amber-300"
                               : "bg-[#F7F3ED] border-[#E8E2D9]";
              const statusLabel = isFullyPaid ? "✓ Interamente pagata"
                                  : isPartial ? "◐ Parzialmente pagata"
                                  : (total > 0 ? "○ In attesa di pagamento" : "");
              const statusColor = isFullyPaid ? "text-emerald-800"
                                  : isPartial ? "text-amber-800"
                                  : "text-[#635F59]";
              if (total <= 0) return null;
              return (
                <div className={`mt-3 border rounded-lg p-3 ${statusBg}`} data-testid="nb-summary-box">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#635F59]">Riepilogo economico</span>
                    {statusLabel && (
                      <span className={`text-[11px] font-medium ${statusColor}`} data-testid="nb-payment-status">{statusLabel}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 text-sm">
                    <span className="text-[#635F59]">Totale prenotazione</span>
                    <span className="font-mono font-bold text-right text-[#1E3A5F]" data-testid="nb-summary-total">€ {total.toFixed(2)}</span>

                    <span className="text-[#635F59]">
                      Acconto atteso <span className="text-[10px] text-[#8B857D]">({rt === "non_refundable" ? "100%" : rt === "deposit_30" ? "30%" : "manuale"})</span>
                    </span>
                    <span className="font-mono text-right text-[#1E3A5F]" data-testid="nb-summary-expected-deposit">€ {expectedDeposit.toFixed(2)}</span>

                    {paidDeposit > 0 && (
                      <>
                        <span className="text-[#635F59]">Acconto incassato</span>
                        <span className="font-mono font-semibold text-right text-emerald-700" data-testid="nb-summary-paid-deposit">€ {paidDeposit.toFixed(2)}</span>
                      </>
                    )}

                    <span className="text-[#635F59] pt-1.5 border-t border-[#E8E2D9]/60">Saldo da pagare</span>
                    <span className="font-mono font-bold text-right text-[#AC5D45] text-base pt-1.5 border-t border-[#E8E2D9]/60" data-testid="nb-summary-balance">€ {balance.toFixed(2)}</span>

                    {form.deposit_method && paidDeposit > 0 && (
                      <>
                        <span className="text-[10px] text-[#8B857D]">Metodo acconto</span>
                        <span className="text-[10px] text-right text-[#635F59]" data-testid="nb-summary-method">{form.deposit_method}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* Allegato ricevuta — opzionale, caricato in chiamata separata
                subito dopo la creazione. NON in edit mode: in quel caso
                la sezione ricevute esistente già gestisce i caricamenti. */}
            {!isEdit && (
              <div className="mt-3" data-testid="nb-receipt-upload">
                <Label className="text-xs">Allega ricevuta di pagamento (opzionale)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="file"
                    accept="image/*,application/pdf"
                    className="h-8 text-xs flex-1"
                    data-testid="nb-receipt-file"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) { setReceiptB64(""); setReceiptName(""); return; }
                      if (f.size > 6 * 1024 * 1024) {
                        toast.error("File troppo grande (max 6MB)");
                        e.target.value = "";
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => {
                        setReceiptB64(String(reader.result || ""));
                        setReceiptName(f.name);
                      };
                      reader.readAsDataURL(f);
                    }}
                  />
                  {receiptName && (
                    <button
                      type="button"
                      onClick={() => { setReceiptB64(""); setReceiptName(""); }}
                      className="text-[10px] text-rose-600 hover:underline"
                      data-testid="nb-receipt-clear"
                    >
                      Rimuovi
                    </button>
                  )}
                </div>
                {receiptName && (
                  <p className="text-[10px] text-emerald-700 mt-1" data-testid="nb-receipt-selected">
                    📎 {receiptName} pronta per il caricamento
                  </p>
                )}
              </div>
            )}
          </div>

          {/* PAGAMENTI MANUALI — visibili solo in modalità modifica */}
          {isEdit && (
            <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3 space-y-3" data-testid="nb-payments-section">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-display text-sm text-[#1E3A5F]">Pagamenti & Tassa di Soggiorno</h4>
                <span className="text-[10px] text-[#635F59] italic">tracking manuale</span>
              </div>

              {/* Riga: prenotazione pagata */}
              <div className="grid sm:grid-cols-3 gap-2 items-end">
                <div className="flex items-center gap-2 pt-5">
                  <Checkbox
                    id="nb-booking-paid"
                    checked={!!form.booking_paid}
                    onCheckedChange={(v) => setForm({ ...form, booking_paid: v === true })}
                    data-testid="nb-booking-paid"
                  />
                  <Label htmlFor="nb-booking-paid" className="text-sm cursor-pointer">Prenotazione pagata</Label>
                </div>
                <div>
                  <Label className="text-xs">Metodo pagamento</Label>
                  <Select
                    value={form.payment_method || "none"}
                    onValueChange={(v) => setForm({ ...form, payment_method: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="nb-payment-method"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="contanti">Contanti</SelectItem>
                      <SelectItem value="bonifico">Bonifico</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="sumup">SumUp</SelectItem>
                      <SelectItem value="satispay">Satispay</SelectItem>
                      <SelectItem value="postepay">PostePay</SelectItem>
                      <SelectItem value="carta">Carta</SelectItem>
                      <SelectItem value="booking_com">Booking.com</SelectItem>
                      <SelectItem value="airbnb">Airbnb</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Riga: tassa di soggiorno */}
              <div className="grid sm:grid-cols-4 gap-2 items-end pt-1 border-t border-[#E8E2D9]">
                <div className="flex items-center gap-2 pt-5">
                  <Checkbox
                    id="nb-tassa-paid"
                    checked={!!form.tassa_paid}
                    onCheckedChange={(v) => setForm({ ...form, tassa_paid: v === true })}
                    data-testid="nb-tassa-paid"
                  />
                  <Label htmlFor="nb-tassa-paid" className="text-sm cursor-pointer">Tassa soggiorno pagata</Label>
                </div>
                <div>
                  <Label className="text-xs">Metodo</Label>
                  <Select
                    value={form.tassa_method || "none"}
                    onValueChange={(v) => setForm({ ...form, tassa_method: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="nb-tassa-method"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="contanti">Contanti</SelectItem>
                      <SelectItem value="bonifico">Bonifico</SelectItem>
                      <SelectItem value="paypal">PayPal</SelectItem>
                      <SelectItem value="sumup">SumUp</SelectItem>
                      <SelectItem value="satispay">Satispay</SelectItem>
                      <SelectItem value="postepay">PostePay</SelectItem>
                      <SelectItem value="carta">Carta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Importo €</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.tassa_amount}
                    onChange={(e) => setForm({ ...form, tassa_amount: e.target.value })}
                    placeholder="es. 3.60"
                    data-testid="nb-tassa-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">Nota</Label>
                  <Input
                    value={form.tassa_note}
                    onChange={(e) => setForm({ ...form, tassa_note: e.target.value })}
                    placeholder="opzionale"
                    data-testid="nb-tassa-note"
                  />
                </div>
              </div>
            </div>
          )}

          {(() => {
            const staticGroupTotal = Number(editingBooking?.group_total || 0);
            const groupTotal = liveGroupTotal != null ? liveGroupTotal : staticGroupTotal;
            const isGroupMaster =
              isEdit
              && (editingBooking?.group_size || 1) > 1
              && (editingBooking?.group_index || 1) === 1
              && groupTotal > 0;
            // Show the group total (€480) on the master row of a multi-
            // room booking, plus a small reminder of this single room's
            // share. Avoids the "saldo €15" trap from the user video.
            // iter182: `liveGroupTotal` è ricalcolato dai siblings live +
            // il previewTotal della camera in edit → sempre corretto.
            return (
              <div className="bg-[#1E3A5F] text-white rounded-lg p-3 flex items-center justify-between">
                <div>
                  <span className="text-xs uppercase tracking-wider opacity-75">
                    {isGroupMaster ? `Totale gruppo (${editingBooking.group_size} camere)` : "Totale stimato"}
                  </span>
                  {isGroupMaster && (
                    <p className="text-[10px] opacity-60 mt-0.5">
                      Questa camera: € {previewTotal.toFixed(2)}
                    </p>
                  )}
                </div>
                <span className="font-display text-2xl" data-testid="nb-total-display">
                  € {(isGroupMaster ? groupTotal : previewTotal).toFixed(2)}
                </span>
              </div>
            );
          })()}

          {/* Ricevute pagamento allegate dall'ospite (sito o App Guida) */}
          {isEdit && editingBooking?.code && (
            <BookingReceiptsList bookingCode={editingBooking.code} />
          )}

          {/* Richieste servizi extra (parrucchiera, delivery, ecc.) dal sito + Guida */}
          {isEdit && editingBooking && Array.isArray(editingBooking.service_requests) && editingBooking.service_requests.length > 0 && (
            <div className="border border-[#E8E2D9] rounded-lg overflow-hidden" data-testid="booking-service-requests">
              <div className="bg-[#FFF8EF] px-3 py-2 text-xs font-semibold text-[#1E3A5F] border-b border-[#E3A857]/40">
                🛎️ Richieste servizi extra ({editingBooking.service_requests.length})
              </div>
              <div className="divide-y divide-[#E8E2D9]">
                {editingBooking.service_requests.map((sr) => (
                  <div key={sr.id} className="p-3 text-xs space-y-1" data-testid={`service-request-${sr.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1E3A5F]">{sr.service_label}</span>
                      {sr.preferred_date && (
                        <span className="text-[#AC5D45] font-mono">
                          {sr.preferred_date}
                          {sr.preferred_time ? ` · ${sr.preferred_time}` : ""}
                        </span>
                      )}
                      <span className="text-[10px] text-stone-400 ml-auto">
                        {(sr.created_at || "").slice(0, 16).replace("T", " ")}
                      </span>
                    </div>
                    {sr.phone && <div className="text-[#635F59]">Tel: {sr.phone}</div>}
                    {sr.notes && <div className="text-[#635F59] italic">&ldquo;{sr.notes}&rdquo;</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* iter196 — "Aggiungi persona/extra" senza toccare l'acconto.
              Solo in modalità edit di una prenotazione confermata/pending. */}
          {isEdit && editingBooking?.id && ["confirmed", "pending", "checked_in"].includes(editingBooking.status) && (
            <AddExtraPersonsWidget
              booking={editingBooking}
              onApplied={(updated) => {
                // Riflette in-place i nuovi valori senza chiudere il dialog
                if (updated?.guests) setForm((f) => ({ ...f, guests: updated.guests }));
                onCreated?.();
                toast.success(`Extra applicato · Nuovo totale €${(updated?.total || 0).toFixed(2)}. Acconto invariato.`);
              }}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="nb-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
            <Save className="w-4 h-4 mr-1" />{busy ? "Salvataggio…" : (isEdit ? "Salva modifiche" : "Crea prenotazione")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/**
 * BookingReceiptsList — shows payment receipts uploaded by the guest (from
 * the public booking page or the Digital Guide app), lets the host preview
 * the image and mark each as Verified / Rejected.
 */
function BookingReceiptsList({ bookingCode }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null); // { url, mime } currently open in lightbox
  const [addingManual, setAddingManual] = useState(false);
  const [manualAmount, setManualAmount] = useState("");
  const [manualPurpose, setManualPurpose] = useState("deposit");
  const [manualMethod, setManualMethod] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualImageB64, setManualImageB64] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Open a receipt in the lightbox. Handles three cases:
  //   1. inline base64 data URI (image/*)  → direct preview
  //   2. inline base64 data URI (PDF)      → decode to blob URL, iframe
  //   3. legacy filesystem receipt         → fetch as blob via api client
  //      (attaches JWT auth header), then iframe/img preview
  const openReceipt = async (r) => {
    try {
      // Case 1 + 2: inline base64
      if (r.receipt_image_b64 && r.receipt_image_b64.startsWith("data:")) {
        const mime = r.receipt_image_b64.substring(5, r.receipt_image_b64.indexOf(";"));
        if (mime.startsWith("image/")) {
          setPreview({ url: r.receipt_image_b64, mime });
          return;
        }
        // PDF or other → convert data URI to blob so <iframe> renders it
        const res = await fetch(r.receipt_image_b64);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPreview({ url, mime, revoke: true });
        return;
      }
      // Case 3: legacy filesystem — fetch via authenticated API client
      if (r.receipt_url || r.source === "website_legacy") {
        const bookingId = r.booking_id;
        const res = await api.get(`/admin/bookings/${bookingId}/receipt`, { responseType: "blob" });
        const mime = r.legacy_mime || res.data.type || "application/octet-stream";
        const blob = new Blob([res.data], { type: mime });
        const url = URL.createObjectURL(blob);
        setPreview({ url, mime, revoke: true, filename: r.legacy_filename });
        return;
      }
      toast.error("Nessun file allegato a questa ricevuta");
    } catch (e) {
      toast.error("Impossibile aprire la ricevuta");
    }
  };

  const closePreview = () => {
    if (preview?.revoke) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/bookings/${encodeURIComponent(bookingCode)}/receipts`);
      setItems(r.data?.items || []);
    } catch (e) {
      console.warn("[receipts] load:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, [bookingCode]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load active payment methods (for the manual-add dropdown)
  useEffect(() => {
    api.get("/payment-methods/public")
      .then((r) => setPaymentMethods(r.data?.items || []))
      .catch(() => setPaymentMethods([]));
  }, []);

  const addManualPayment = async () => {
    const amt = parseFloat(manualAmount);
    if (!amt || amt <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    setBusy("manual");
    try {
      // We piggy-back on the public upload-receipt endpoint to keep one
      // single insertion path — the receipt is marked `source=website` but
      // labelled "Inserito manualmente" via note.
      // Empty file is allowed (a placeholder 1×1 transparent png).
      const placeholder = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgAAIAAAUAAeImBZsAAAAASUVORK5CYII=";
      await api.post(`/bookings/${encodeURIComponent(bookingCode)}/upload-receipt`, {
        booking_code: bookingCode,
        method_id: manualMethod || null,
        amount: amt,
        purpose: manualPurpose,
        purpose_detail: manualNote || "Inserito manualmente dall'host",
        receipt_image_b64: manualImageB64 || placeholder,
        guest_name: null,
        note: "manual_entry",
      });
      // Auto-mark as verified since the host is the one entering it
      // (find the just-created receipt by reload + take the newest with source=website pending)
      await reload();
      toast.success(`Pagamento di €${amt.toFixed(2)} registrato`);
      setAddingManual(false);
      setManualAmount("");
      setManualMethod("");
      setManualNote("");
      setManualImageB64("");
      setManualPurpose("deposit");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setBusy(null);
    }
  };

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/admin/receipts/${id}/${action}`);
      toast.success(action === "confirm" ? "Pagamento confermato" : "Ricevuta rifiutata");
      await reload();
    } catch (e) {
      toast.error("Errore");
      console.warn("[receipts] action:", e);
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="text-xs text-stone-500 py-2">Carico ricevute…</div>;

  const STATUS_TONE = {
    pending_review: "bg-amber-50 text-amber-800 border-amber-200",
    verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rejected: "bg-red-50 text-red-800 border-red-200",
  };
  const STATUS_LABEL = {
    pending_review: "In verifica",
    verified: "Pagamento confermato",
    rejected: "Rifiutata",
  };
  const PURPOSE_LABEL = {
    deposit: "Acconto",
    balance: "Saldo",
    tassa_soggiorno: "Tassa di soggiorno",
    extra_service: "Servizio extra",
    other: "Altro",
  };
  const totalPaid = items.filter((r) => r.status === "verified").reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="border border-[#E8E2D9] rounded-lg overflow-hidden" data-testid="booking-receipts-list">
      <div className="bg-[#FDFBF7] px-3 py-2 text-xs font-semibold text-[#1E3A5F] border-b border-[#E8E2D9] flex items-center justify-between">
        <span>💰 Pagamenti / Ricevute ({items.length}) · Confermati: €{totalPaid.toFixed(2)}</span>
        {!addingManual && (
          <button
            type="button"
            onClick={() => setAddingManual(true)}
            className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[#AC5D45] text-[#AC5D45] hover:bg-[#AC5D45]/5"
            data-testid="add-manual-payment-btn"
          >
            + Aggiungi pagamento
          </button>
        )}
      </div>

      {addingManual && (
        <div className="p-3 bg-amber-50/60 border-b border-amber-200 space-y-2" data-testid="manual-payment-form">
          <div className="text-xs font-semibold text-[#1E3A5F]">Registra pagamento ricevuto manualmente</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px]">Importo €</Label>
              <Input
                type="number"
                step="0.01"
                value={manualAmount}
                onChange={(e) => setManualAmount(e.target.value)}
                placeholder="es. 30.00"
                className="h-8 text-sm"
                data-testid="manual-pay-amount"
              />
            </div>
            <div>
              <Label className="text-[11px]">Causale</Label>
              <Select value={manualPurpose} onValueChange={setManualPurpose}>
                <SelectTrigger className="h-8 text-sm" data-testid="manual-pay-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deposit">Acconto</SelectItem>
                  <SelectItem value="balance">Saldo</SelectItem>
                  <SelectItem value="tassa_soggiorno">Tassa di soggiorno</SelectItem>
                  <SelectItem value="extra_service">Servizio extra</SelectItem>
                  <SelectItem value="other">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Metodo</Label>
            <Select value={manualMethod} onValueChange={setManualMethod}>
              <SelectTrigger className="h-8 text-sm" data-testid="manual-pay-method"><SelectValue placeholder="— scegli —" /></SelectTrigger>
              <SelectContent>
                {paymentMethods.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Note (opzionale)</Label>
            <Input
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              placeholder="es. Bonifico ricevuto il 10/06 — conferma da SMS banca"
              className="h-8 text-sm"
              data-testid="manual-pay-note"
            />
          </div>
          <div>
            <Label className="text-[11px]">Allegato (opzionale — screenshot/PDF della ricevuta)</Label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
                const rd = new FileReader();
                rd.onload = () => setManualImageB64(String(rd.result || ""));
                rd.readAsDataURL(f);
              }}
              className="text-xs"
              data-testid="manual-pay-file"
            />
            {manualImageB64 && <div className="text-[10px] text-emerald-700 mt-1">✓ Allegato caricato</div>}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="ghost" onClick={() => setAddingManual(false)} className="h-7 text-xs">Annulla</Button>
            <Button
              size="sm"
              onClick={addManualPayment}
              disabled={busy === "manual"}
              className="h-7 text-xs bg-[#AC5D45] hover:bg-[#8A4936] text-white"
              data-testid="manual-pay-save"
            >
              {busy === "manual" ? "Salvo…" : "Registra"}
            </Button>
          </div>
        </div>
      )}

      {!items.length ? (
        <div className="bg-stone-50 border-dashed p-3 text-xs text-stone-500 text-center">
          Nessuna ricevuta caricata dall&apos;ospite. Usa &quot;+ Aggiungi pagamento&quot; per registrare manualmente.
        </div>
      ) : (
      <div className="divide-y divide-[#E8E2D9]">
        {items.map((r) => {
          const isImg = r.receipt_image_b64?.startsWith("data:image") ||
                        (r.source === "website_legacy" && r.legacy_mime?.startsWith("image/"));
          const isLegacy = r.source === "website_legacy";
          const filename = r.legacy_filename;
          return (
          <div key={r.id} className="p-3 flex gap-3 items-start" data-testid={`receipt-${r.id}`}>
            {/* Thumb — always clickable, opens image OR PDF OR legacy filesystem receipt */}
            <button
              type="button"
              onClick={() => openReceipt(r)}
              className="w-16 h-16 rounded border border-[#E8E2D9] overflow-hidden bg-white shrink-0 hover:border-[#AC5D45] flex items-center justify-center"
              aria-label="Apri ricevuta"
              data-testid={`receipt-thumb-${r.id}`}
            >
              {isImg && r.receipt_image_b64 ? (
                <img src={r.receipt_image_b64} alt="ricevuta" className="w-full h-full object-cover" />
              ) : (
                <div className="text-[10px] text-stone-500 flex flex-col items-center gap-0.5 px-1 text-center">
                  <FileText className="w-4 h-4" />
                  <span>{isImg ? "IMG" : "PDF"}</span>
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                <span className="font-semibold text-sm text-[#1E3A5F]">€{Number(r.amount || 0).toFixed(2)}</span>
                <span className="text-xs text-[#635F59]">{PURPOSE_LABEL[r.purpose] || r.purpose}</span>
                {r.method_label && <span className="text-xs text-[#635F59]">· {r.method_label}</span>}
                <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${STATUS_TONE[r.status] || ""}`}>
                  {STATUS_LABEL[r.status] || r.status}
                </span>
                <span className="text-[10px] text-stone-400 ml-auto">
                  {r.source === "guide_app" ? "📱 Guida" : "🌐 Sito"} · {(r.created_at || "").slice(0, 16).replace("T", " ")}
                </span>
              </div>
              {isLegacy && filename && (
                <div className="text-xs text-[#AC5D45] mb-1 inline-flex items-center gap-1">
                  <FileText className="w-3 h-3" /> {filename}
                </div>
              )}
              {r.purpose_detail && (
                <div className="text-xs text-[#635F59] italic">{r.purpose_detail}</div>
              )}
              {r.guest_name && (
                <div className="text-xs text-stone-500">Da: {r.guest_name}</div>
              )}
              <button
                type="button"
                onClick={() => openReceipt(r)}
                className="text-xs text-[#AC5D45] hover:underline mt-1 inline-flex items-center gap-1"
                data-testid={`receipt-open-${r.id}`}
              >
                <ExternalLink className="w-3 h-3" /> Apri ricevuta
              </button>
              {r.status === "pending_review" && !isLegacy && (
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    onClick={() => act(r.id, "confirm")}
                    disabled={busy === r.id}
                    className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`receipt-confirm-${r.id}`}
                  >
                    ✓ Conferma pagamento
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => act(r.id, "reject")}
                    disabled={busy === r.id}
                    className="h-7 px-3 text-xs"
                    data-testid={`receipt-reject-${r.id}`}
                  >
                    ✕ Rifiuta
                  </Button>
                </div>
              )}
            </div>
          </div>
        );})}
      </div>
      )}

      {/* Lightbox preview — supports both image and PDF (iframe) */}
      {preview && (
        <div
          className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="receipt-lightbox"
        >
          <div
            className="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#E8E2D9] bg-[#FDFBF7]">
              <div className="text-sm text-[#1E3A5F] font-semibold truncate">
                {preview.filename || "Ricevuta"}
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={preview.url}
                  download={preview.filename || "ricevuta"}
                  className="text-xs text-[#AC5D45] hover:underline inline-flex items-center gap-1"
                  data-testid="receipt-lightbox-download"
                >
                  <Download className="w-3.5 h-3.5" /> Scarica
                </a>
                <button
                  type="button"
                  onClick={closePreview}
                  className="text-stone-500 hover:text-stone-800 text-xl leading-none"
                  aria-label="Chiudi"
                  data-testid="receipt-lightbox-close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-stone-100 flex items-center justify-center">
              {preview.mime?.startsWith("image/") ? (
                <img src={preview.url} alt="ricevuta" className="max-w-full max-h-full" />
              ) : (
                <iframe src={preview.url} title="ricevuta" className="w-full h-[80vh] bg-white" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
